import React, { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { Lock, Prohibit, TrendUp, Sliders, MagnifyingGlass, X, CaretUp, CaretDown, CaretUpDown, Crosshair, Warning, Target, DownloadSimple, Sparkle, ArrowCounterClockwise, Star, Keyboard, Crown } from "@phosphor-icons/react";
import ChalkWatchWidget from "./ChalkWatchWidget";
import { api } from "@/lib/api";
import { PlayerHeadshot, useHeadshotMap } from "@/components/TeamVisuals";
import { teamTextColor } from "@/lib/teams";
import { useAuth } from "@/lib/auth";

// Numeric extractor per sortable column. Falls back through the DK pool's
// known projection columns so offseason data (avg_ppg only) still sorts.
// NOTE: uses "first positive" semantics rather than JS `??` — the DK pool
// often returns `market_mean_proj: 0` (real integer zero, not null) during
// offseason. `??` would lock onto that 0 and never reach avg_ppg, silently
// breaking sort-by-proj and sort-by-value. `firstPositive` skips zeros so
// the sort matches the row render (which also skips zeros).
const firstPositive = (...vals) => {
  for (const v of vals) {
    if (v === null || v === undefined) continue;
    const n = Number(v);
    if (!Number.isNaN(n) && n > 0) return n;
  }
  return 0;
};
// Sprint 67 — OUT / IR / Doubtful / PUP / Suspended players project 0 and
// live in the Removed tab (they never enter the build), regardless of the
// DK avg-ppg fallback the display would otherwise reach for.
const OUT_STATUSES = new Set(["O", "OUT", "D", "DOUBTFUL", "IR", "PUP", "SUS", "SUSP", "SUSPENDED", "PS", "FA"]);
const RESTORED = new Set(); // dk_ids the user pulled back into the active pool (override.restore)
// A player the pool pipeline deliberately zeroed (backup QB / injury / roster gate) is unplayable too.
export const isOut = (p) => (OUT_STATUSES.has(String(p?.injury_status || "").toUpperCase().trim()) || !!p?.unplayable_reason) && !RESTORED.has(String(p?.dk_id));
const projOf = (p) => (isOut(p) ? 0 : firstPositive(p.our_proj, p.projection, p.market_mean_proj, p.avg_ppg));
// Industry-standard DFS value formula: (Projection / Salary) × 1000.
// Example: 27.7 proj / $7,800 × 1000 = 3.55 value.
// Guarded against zero-salary edge cases (would divide by zero).
const valueOf = (p) => {
  const sal = Number(p.salary || 0);
  if (sal <= 0) return 0;
  return (projOf(p) / sal) * 1000;
};

const SORT_ACCESSORS = {
  name:    (p) => (p.name || "").toLowerCase(),
  salary:  (p) => Number(p.salary || 0),
  proj:    projOf,
  value:   valueOf,
  own:     (p) => firstPositive(p.market_mean_own, p.our_own),
  ceiling: (p) => ceilOf(p),
  leverage: (p) => levOf(p),
  levscore: (p) => levScoreOf(p) ?? -999,
  mkt:     (p) => mktDeltaOf(p) ?? -999,
};

// Sprint 90 — Leverage Score (SaberSim-comparable): sim Optimal% minus the
// field's REAL ownership (recounted from imported field lineups when we have
// them), dupe-scaled by the backend. Signed: + = we're leveraged for, - = the
// field is overweight. `null` until the backend has run the slate sims.
const levScoreOf = (p) => (p?.leverage_delta === null || p?.leverage_delta === undefined
  ? null : Number(p.leverage_delta));
const levScoreColor = (v) => {
  if (v == null) return "text-zinc-700";
  if (v >= 10) return "text-[#39FF14]";
  if (v >= 4) return "text-[#9EFF6A]";
  if (v <= -10) return "text-[#FF3B30]";
  if (v <= -4) return "text-[#FF8A00]";
  return "text-zinc-400";
};

// Sprint 62.1 — Cockpit columns. Ceiling mirrors the simulator's 90th-pct
// (position CV × 1.28); Leverage = ceiling ÷ ownership (the number a
// grinder computes in their head — high ceiling the field isn't on).
const POS_CV = { QB: 0.28, RB: 0.36, WR: 0.42, TE: 0.46, DST: 0.52, K: 0.40 };
const ceilOf = (p) => {
  const c = Number(p.ceiling || 0);
  if (c > 0) return c;
  const proj = projOf(p);
  return proj > 0 ? proj * (1 + 1.2816 * (POS_CV[(p.position || "").toUpperCase()] ?? 0.40)) : 0;
};
const levOf = (p) => {
  const c = ceilOf(p);
  if (c <= 0) return 0;
  return c / Math.max(0.5, firstPositive(p.market_mean_own, p.our_own));
};
// Sprint 88 — Showdown slot view. The crown is effectively a different player:
// 1.5x salary, the site's captain projection (or 1.5x) and its OWN ownership.
const cptMultOf = (p) => {
  const fp = Number(p.field_proj || 0);
  const fpc = Number(p.field_proj_cpt || 0);
  if (fp > 0 && fpc > 0) return Math.min(1.8, Math.max(1.3, fpc / fp));
  return 1.5;
};
const cptOwnOf = (p) => (Number(p.field_own_cpt || 0) > 0
  ? Number(p.field_own_cpt)
  : firstPositive(p.market_mean_own, p.our_own));
const flexOwnOf = (p) => (Number(p.field_own_flex || 0) > 0
  ? Number(p.field_own_flex)
  : firstPositive(p.market_mean_own, p.our_own));

const mktDeltaOf = (p) => {
  const ours = firstPositive(p.our_proj, p.projection);
  const mkt = Number(p.market_mean_proj || 0);
  if (ours <= 0 || mkt <= 0) return null;
  return ours - mkt;
};
const levColor = (v) => {
  if (!v || v <= 0) return "text-zinc-600";
  if (v >= 4.0) return "text-[#39FF14]";
  if (v >= 2.0) return "text-white";
  return "text-zinc-500";
};

// Color band for the VAL cell:
//   ≥ 3.0 → soft neon green (elite pt/$ savers — chase these)
//   2.0-2.99 → white (standard)
//   < 2.0 → muted grey (overpriced / avoid)
// 0 (no projection yet) also renders as muted so offseason pools stay quiet.
const valueColor = (v) => {
  if (!v || v <= 0) return "text-zinc-600";
  if (v >= 3.0) return "text-[#39FF14]";
  if (v >= 2.0) return "text-white";
  return "text-zinc-600";
};

export default function PlayerPool({ overrides, onChange, showdown = false }) {
  const headshots = useHeadshotMap();
  useMemo(() => {
    RESTORED.clear();
    for (const [k, v] of Object.entries(overrides || {})) if (v && v.restore) RESTORED.add(String(k));
  }, [overrides]);
  const { user } = useAuth();
  const isAdmin = !!user?.is_admin;
  const [pool, setPool] = useState([]);
  // Sprint 60.105 — reactive game-lock filter. The Game Lock board
  // above the pool writes to localStorage[`wp_excluded_games`] and
  // fires `wp:excluded-games` on every toggle. We mirror that state
  // here so the pool only shows players from LOCKED games (i.e. games
  // NOT in the exclusion set), matching what the ILP will actually see.
  const [excludedGames, setExcludedGames] = useState(() => {
    try {
      const raw = localStorage.getItem("wp_excluded_games");
      return raw ? new Set(JSON.parse(raw)) : new Set();
    } catch { return new Set(); }
  });
  useEffect(() => {
    const h = (e) => {
      const arr = e?.detail?.excluded ?? (() => {
        try { return JSON.parse(localStorage.getItem("wp_excluded_games") || "[]"); }
        catch { return []; }
      })();
      setExcludedGames(new Set(arr));
    };
    window.addEventListener("wp:excluded-games", h);
    return () => window.removeEventListener("wp:excluded-games", h);
  }, []);
  const [query, setQuery] = useState("");
  const [posFilter, setPosFilter] = useState("ALL");
  // Sprint 88 — showdown only: view the pool as CPT (1.5x salary, captain
  // projection + captain ownership) or as FLEX. Sorting follows the view.
  const [slotView, setSlotView] = useState("FLEX");
  const [loading, setLoading] = useState(false);
  // Default view: highest-projected first (users' most common ask)
  const [sortBy, setSortBy] = useState("proj");
  const [sortDir, setSortDir] = useState("desc");
  // Leverage Radar: toggle to surface sub-5%-owned players whose projection
  // sits in the top quartile of the current pool. Fastest way to find
  // GPP-worthy contrarian pivots without eyeballing the sorted list.
  const [leverageOn, setLeverageOn] = useState(false);
  // Top Value quick filter — surfaces the top 20% by (projection ÷ salary) × 1000
  // within the current filtered view. Cash-game builders scanning for salary
  // floor plays hit this once instead of manually sorting + guessing a cutoff.
  const [topValueOn, setTopValueOn] = useState(false);
  // Bulk exposure setter inputs. Applies min/max exposure to every player in
  // the current position filter scope (ALL = every projected player). This is
  // the "give me 25% Max Exp on all my QBs" bulk action — replaces the
  // otherwise painful 12-input, one-at-a-time workflow.
  const [bulkMin, setBulkMin] = useState("");
  const [bulkMax, setBulkMax] = useState("");
  // Bulk projection floor — flips `exclude=true` on every player in the
  // current scope whose model projection is below this value. Preseason /
  // Week 1 slates dump ~200 low-projected backups (2-11 pts) into the pool;
  // this is the "purge backups in one click" flow. Empty = no-op.
  const [bulkMinProj, setBulkMinProj] = useState("");
  // Removed Players tab — toggles the table between the ACTIVE pool
  // (default) and the list of players the user has excluded (manually or
  // via the Min Proj bulk purge). In the Removed view, action buttons swap
  // to a per-row Restore + a header Restore All so the user can walk
  // things back without hunting through a filtered pool.
  const [showRemoved, setShowRemoved] = useState(false);
  // Restore Guard: OUT/IR/bench rows open an inline confirm that demands a projection.
  const [restoreGuard, setRestoreGuard] = useState(null);   // { dkId, proj }
  // Bulk-scope override: when ON, bulk apply/clear operates on the ENTIRE
  // pool regardless of the top table's position filter. Lets users QB-filter
  // the table for readability while still bulk-editing every position in
  // one shot — was previously impossible without also flipping the top
  // filter to ALL (which reset the sorted view).
  const [bulkAllPool, setBulkAllPool] = useState(false);

  // Sticky-scope guard: if the user switches the top position filter AFTER
  // the "Entire pool" toggle was on, auto-drop the toggle so their next
  // Bulk Apply follows the position they just navigated to. Without this,
  // users hit "Entire pool → cap everyone at 40%", then switched to RB and
  // typed a different Max — expecting it to hit only RBs — and instead
  // clobbered every position in the pool again. Toggle now acts like a
  // single-use override, not sticky mode.
  useEffect(() => {
    if (bulkAllPool) setBulkAllPool(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [posFilter]);
  // Dual-Threat QB weekly workflow guide modal — opened from the DT banner
  // above the pool. Explains the whole mechanic in one glance so users know
  // to update their flags every week (see DualThreatGuideModal below).
  const [showDtGuide, setShowDtGuide] = useState(false);
  // Icon legend strip — quick "what do these icons do" cheatsheet for the
  // three action columns (Lock / Exclude / DT). Dismissible so power users
  // can hide it; new users see it by default until they close it. LS-backed.
  const [showIconLegend, setShowIconLegend] = useState(() => {
    try { return localStorage.getItem("wp_hide_pool_icon_legend") !== "1"; }
    catch (_) { return true; }
  });
  const dismissIconLegend = () => {
    setShowIconLegend(false);
    try { localStorage.setItem("wp_hide_pool_icon_legend", "1"); } catch (_) { /* private mode */ }
  };
  // Threshold for the red-flag icon: any player whose chalk_alert is within
  // this fraction of the pool's peak alert gets flagged. 0.85 = the "red"
  // band from ChalkWatchWidget's traffic-light scale, keeping the two views
  // aligned. Tune together with `alertColors()` in ChalkWatchWidget.jsx.
  const RED_FLAG_THRESHOLD = 0.85;
  // Red-flag set: players whose chalk_alert is in the top ~15% of the current
  // pool (>= RED_FLAG_THRESHOLD × max_alert). Loaded from the same
  // /api/ai/chalk-watch endpoint the Chalk Watch widget uses so the two views
  // stay in lock-step. Storing dk_ids keeps name-collision-safe matching
  // against pool rows.
  const [redFlagged, setRedFlagged] = useState(() => new Set());
  // Which chalk player's pivot popover is currently open (`null` = closed).
  // Clicking the red-flag icon toggles this; clicking anywhere else closes.
  const [pivotFor, setPivotFor] = useState(null);
  // Sprint 62.1 — keyboard cockpit: ↑/↓ move the row cursor, L lock,
  // X exclude, F like (+10%), D dual-threat (QB only), Esc clears.
  const [focusIdx, setFocusIdx] = useState(-1);
  const tableRef = useRef(null);
  const focusIdxRef = useRef(-1);
  focusIdxRef.current = focusIdx;


  useEffect(() => {
    let cancelled = false;
    const loadFlags = async () => {
      try {
        // Widened limit — the pool is ~50-150 players; 30 comfortably covers
        // the "red" tier (top ~15%) for any realistic slate size.
        const r = await api.aiChalkWatch(30);
        if (cancelled) return;
        const rows = r.rows || [];
        const max = r.max_alert || 0;
        if (max <= 0 || rows.length === 0) {
          setRedFlagged(new Set());
          return;
        }
        const ids = new Set();
        for (const row of rows) {
          if (Number(row.chalk_alert || 0) >= RED_FLAG_THRESHOLD * max) {
            const key = row.dk_id || row.name;
            if (key) ids.add(String(key));
          }
        }
        setRedFlagged(ids);
      } catch (e) { /* silent — flags are best-effort UI */ }
    };
    loadFlags();
    const onEvent = () => loadFlags();
    window.addEventListener("wp:consensus-advisor-auto", onEvent);
    window.addEventListener("wp:pool-refreshed", onEvent);
    return () => {
      cancelled = true;
      window.removeEventListener("wp:consensus-advisor-auto", onEvent);
      window.removeEventListener("wp:pool-refreshed", onEvent);
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    const loadPool = async () => {
      setLoading(true);
      try {
        // Sprint 60.113 — bumped limit from 500 → 1200. 12-game Main
        // slates run ~716 players, Sunday-Millionaire and 14G swaps go up
        // to ~850. Prior 500 cap silently truncated the pool, hiding
        // ~200 players from locks/excludes/exposure controls even though
        // the ILP saw the full pool via state.dk_pool.
        const r = await api.getPool(1200);
        if (!cancelled) setPool(r.rows || []);
      } catch (e) {
        // pool empty until DK uploaded
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    loadPool();
    // Refresh when the pool changes upstream (mock-slate load, DK upload,
    // auto-ingester tick). Without this, the table stays frozen on stale
    // rows until the user manually reloads the page.
    const onPoolEvent = () => loadPool();
    window.addEventListener("wp:pool-refreshed", onPoolEvent);
    return () => {
      cancelled = true;
      window.removeEventListener("wp:pool-refreshed", onPoolEvent);
    };
  }, []);

  const toggleExclude = (p, ov) => {
    if (ov.exclude) {
      patchOverrides([{ dkId: p.dk_id, key: "exclude", value: false }, { dkId: p.dk_id, key: "exclude_reason", value: null }]);
      return;
    }
    setField(p.dk_id, "exclude", true);
    if (showdown && !isOut(p) && Number(p.our_proj ?? p.projection ?? 0) > 0) {
      toast.info(`${p.name} faded from every lineup`, {
        description: "Showdown pool is ~25 bodies — a full fade shrinks the lineup space and pushes your builds toward the field's. If it's a leverage call, MAX EXP (e.g. 10%) keeps him as a rare punt instead.",
        duration: 9000,
      });
    }
  };

  const setField = (dkId, key, value) => {
    const cur = overrides[dkId] || {};
    const next = { ...cur, [key]: value };
    const cleaned = Object.fromEntries(Object.entries(next).filter(([k, v]) =>
      v !== null && v !== undefined && v !== "" && v !== false
    ));
    const newOv = { ...overrides };
    if (Object.keys(cleaned).length === 0) delete newOv[dkId];
    else newOv[dkId] = cleaned;
    onChange(newOv);
  };

  // Batched override patch — used by any flow that needs to update multiple
  // players in one tick (e.g. the pivot-swap popover, which excludes the
  // chalk AND locks the pivot). setField reads the closure-captured
  // `overrides` snapshot, so calling it 3-4x in the same tick would lose
  // all but the last write. patchOverrides builds one merged object and
  // fires onChange once.  `patches` shape: [{ dkId, key, value }, ...]
  const patchOverrides = (patches) => {
    const newOv = { ...overrides };
    for (const { dkId, key, value } of patches) {
      const cur = newOv[dkId] || {};
      const next = { ...cur, [key]: value };
      const cleaned = Object.fromEntries(Object.entries(next).filter(([k, v]) =>
        v !== null && v !== undefined && v !== "" && v !== false
      ));
      if (Object.keys(cleaned).length === 0) delete newOv[dkId];
      else newOv[dkId] = cleaned;
    }
    onChange(newOv);
  };

  const clearFilters = () => {
    setLeverageOn(false);
    setTopValueOn(false);
    setPosFilter("ALL");
    setQuery("");
  };

  const clearAll = () => {
    const n = Object.keys(overrides).length;
    // Sprint 60.108 — Reset Pool now ALSO clears the view-only filters
    // (Leverage Radar, Top Value, position filter, search). Users kept
    // hitting Reset Pool while Leverage+TopValue were both on, seeing
    // "no plays" and thinking their pool was corrupted.
    if (n === 0) {
      clearFilters();
      toast.info("Filters cleared — pool defaults restored.");
      return;
    }
    if (window.confirm(
      `Reset Player Pool?\n\nThis will clear ALL ${n} override${n === 1 ? "" : "s"}:\n` +
      "  • Locks & Excludes\n" +
      "  • Dual-Threat QB flags\n" +
      "  • Min / Max Exposure caps\n" +
      "  • Boost adjustments\n" +
      "  • Projection edits (restored to model defaults)\n\n" +
      "Also clears Leverage Radar + Top Value + position filter + search.\n" +
      "Locks, excludes, and DT flags on the slate itself are unaffected."
    )) {
      onChange({});
      clearFilters();
      toast.success(`Player Pool reset · ${n} override${n === 1 ? "" : "s"} cleared`);
    }
  };

  // Bulk exposure apply — writes `min_exposure_pct` and/or `max_exposure_pct`
  // to every player in the current position scope (ALL or a specific pos).
  // Applied against the FULL pool (not just the 100-row view cap or the
  // search-filtered subset) — the user's intent is "all my QBs", not "the
  // 12 QBs currently visible below". Empty inputs = no-op for that field so
  // the user can set only Min or only Max without wiping the other.
  const applyBulkExposure = () => {
    // Effective scope respects the "Entire pool" toggle — when ON we ignore
    // the top posFilter (still applied to the visible table) and hit every
    // player. This is the "cap everyone at 30% Max Exp in one click" flow.
    const effectivePos = bulkAllPool ? "ALL" : posFilter;
    const scope = pool.filter((p) => effectivePos === "ALL" || p.position === effectivePos);
    if (scope.length === 0) {
      toast.error("No players in the current filter scope. Load a slate first.");
      return;
    }
    const minVal = bulkMin === "" ? null : Number(bulkMin);
    const maxVal = bulkMax === "" ? null : Number(bulkMax);
    if (minVal === null && maxVal === null) {
      toast.error("Enter a Min or Max value first (or both).");
      return;
    }
    // Sanity guardrail so the user can't lock the ILP into an infeasible
    // portfolio (e.g. min 80% floor on every WR while only picking 3 per lineup).
    if (minVal !== null && (minVal < 0 || minVal > 100)) {
      toast.error("Min exposure must be 0-100%.");
      return;
    }
    if (maxVal !== null && (maxVal < 0 || maxVal > 100)) {
      toast.error("Max exposure must be 0-100%.");
      return;
    }
    if (minVal !== null && maxVal !== null && minVal > maxVal) {
      toast.error("Min exposure cannot exceed Max exposure.");
      return;
    }
    const patches = [];
    for (const p of scope) {
      if (minVal !== null) patches.push({ dkId: p.dk_id, key: "min_exposure_pct", value: minVal });
      if (maxVal !== null) patches.push({ dkId: p.dk_id, key: "max_exposure_pct", value: maxVal });
    }
    patchOverrides(patches);
    const label = effectivePos === "ALL" ? "all projected players" : `all ${effectivePos}s`;
    const parts = [];
    if (minVal !== null) parts.push(`Min ${minVal}%`);
    if (maxVal !== null) parts.push(`Max ${maxVal}%`);
    toast.success(`Applied ${parts.join(" + ")} to ${scope.length} ${label}.`);
    setBulkMin("");
    setBulkMax("");
  };

  // Bulk exposure clear — strips min/max exposure entries from every player
  // in the current filter scope, preserving all other overrides (locks,
  // excludes, boosts, projection edits). Useful for undoing a slate-wide cap
  // without hitting the nuclear CLEAR ALL button.
  const clearBulkExposure = () => {
    const effectivePos = bulkAllPool ? "ALL" : posFilter;
    const scope = pool.filter((p) => effectivePos === "ALL" || p.position === effectivePos);
    const patches = [];
    for (const p of scope) {
      const cur = overrides[String(p.dk_id)] || {};
      if (cur.min_exposure_pct != null && cur.min_exposure_pct !== "") {
        patches.push({ dkId: p.dk_id, key: "min_exposure_pct", value: null });
      }
      if (cur.max_exposure_pct != null && cur.max_exposure_pct !== "") {
        patches.push({ dkId: p.dk_id, key: "max_exposure_pct", value: null });
      }
    }
    if (patches.length === 0) {
      toast.info("No exposure caps to clear in this scope.");
      return;
    }
    patchOverrides(patches);
    const label = effectivePos === "ALL" ? "the pool" : `all ${effectivePos}s`;
    toast.success(`Cleared ${patches.length} exposure cap${patches.length === 1 ? "" : "s"} across ${label}.`);
  };

  // Value Auto-Tune — one-click cash/floor build. Nudges the ILP solver toward
  // salary savers by applying:
  //   +15% boost_pct to the top 10% of the pool by (proj / salary) × 1000
  //   -15% boost_pct to the bottom 10% (excludes zero-value / no-projection rows)
  // Composes with ANY preset (Cash 50/50 for floor-first, Milly Maker for value-
  // aware GPP). Also auto-satisfies the DFS Two-Touch compliance rule since it
  // writes at least 2 substantive overrides. Idempotent — running it twice
  // replaces the previous batch instead of stacking.
  const valueAutoTune = () => {
    const withVal = pool
      .map((p) => ({ dk_id: p.dk_id, value: valueOf(p), pos: p.position, name: p.name }))
      .filter((p) => p.value > 0);
    if (withVal.length < 10) {
      toast.error("Not enough projected players to auto-tune. Upload the DK slate first.");
      return;
    }
    withVal.sort((a, b) => b.value - a.value);
    const slice = Math.max(3, Math.floor(withVal.length * 0.10));
    const topN = withVal.slice(0, slice);
    const bottomN = withVal.slice(-slice);

    // Preserve any existing overrides the user has already made; only overwrite
    // `boost_pct` for the top/bottom targets. This lets a user lock a favorite
    // and still auto-tune around them without losing the lock.
    const newOv = { ...overrides };
    const write = (id, boost) => {
      const cur = newOv[id] || {};
      const next = { ...cur, boost_pct: boost };
      const cleaned = Object.fromEntries(Object.entries(next).filter(([, v]) =>
        v !== null && v !== undefined && v !== "" && v !== false
      ));
      if (Object.keys(cleaned).length === 0) delete newOv[id];
      else newOv[id] = cleaned;
    };
    for (const p of topN) write(p.dk_id, 15);
    for (const p of bottomN) write(p.dk_id, -15);
    onChange(newOv);
    toast.success(
      `Value Auto-Tune applied — +15% to top ${slice} value plays (${topN[0].name}…), −15% to bottom ${slice}.`
    );
  };

  // Bulk projection floor — flag every player in the current scope whose
  // model projection is below `bulkMinProj` as excluded (via the same
  // `exclude` override the row-level ✕ button uses). Composes cleanly with
  // the Removed tab: after the purge, the user can jump to Removed to see
  // exactly who was pulled and restore anyone who's actually in play. The
  // scope respects the Entire Pool override — same rules as Min/Max Exp.
  const applyBulkMinProj = () => {
    if (bulkMinProj === "" || bulkMinProj === null) {
      toast.error("Enter a Min Proj value first.");
      return;
    }
    const threshold = Number(bulkMinProj);
    if (!Number.isFinite(threshold) || threshold < 0) {
      toast.error("Min Proj must be a non-negative number.");
      return;
    }
    const effectivePos = bulkAllPool ? "ALL" : posFilter;
    const scope = pool.filter((p) => effectivePos === "ALL" || p.position === effectivePos);
    if (scope.length === 0) {
      toast.error("No players in the current filter scope. Load a slate first.");
      return;
    }
    // Only flip players that (a) have a projection below the threshold and
    // (b) aren't already locked (locking someone and excluding them is an
    // infeasible ILP state — we skip locked rows so the user's explicit
    // "always in" wins over a bulk floor).
    //
    // Sprint 60.141 — Starter safeguard. Some real NFL starters (rookies,
    // late-week Q-status players, Thursday-only feed lag) get 0.0
    // projections from SGO even though they're on active rosters. A
    // naive "purge below X" sweeps them along with real dart-throw
    // benchers, which the user (rightly) reports as a broken workflow.
    // Skip rows where salary >= $4,000 AND proj === 0 — bench players
    // are almost always <$4k on DK NFL, so the (0 proj + $4k+ salary)
    // combo is a data-gap signal, not a low-upside benchmark.
    const STARTER_SALARY_FLOOR = 4000;
    const patches = [];
    let skippedLocked = 0;
    let skippedStarters = 0;
    for (const p of scope) {
      const cur = overrides[String(p.dk_id)] || {};
      if (cur.lock) { skippedLocked += 1; continue; }
      const proj = projOf(p);
      if (proj >= threshold) continue;
      if (proj === 0 && Number(p.salary || 0) >= STARTER_SALARY_FLOOR) {
        skippedStarters += 1;
        continue;
      }
      patches.push({ dkId: p.dk_id, key: "exclude", value: true });
      patches.push({ dkId: p.dk_id, key: "exclude_reason", value: `purge<${threshold}` });
    }
    if (patches.length === 0) {
      const detail = skippedStarters > 0
        ? `${skippedStarters} likely-starter${skippedStarters === 1 ? "" : "s"} @ $4k+ w/ 0 proj skipped — SGO probably hasn't posted yet.`
        : `No players below ${threshold} proj in this scope.`;
      toast.info(detail);
      return;
    }
    patchOverrides(patches);
    const label = effectivePos === "ALL" ? "the pool" : `${effectivePos}s`;
    const skipTail = [
      skippedLocked > 0 ? `${skippedLocked} locked` : null,
      skippedStarters > 0 ? `${skippedStarters} starter${skippedStarters === 1 ? "" : "s"} @ $4k+ (0 proj)` : null,
    ].filter(Boolean).join(" · ");
    toast.success(
      `Removed ${patches.length} player${patches.length === 1 ? "" : "s"} below ${threshold} proj from ${label}${skipTail ? ` · ${skipTail} skipped` : ""}.`,
      skippedStarters > 0 ? {
        description: `SGO data gap probably explains why those $4k+ names show 0 — restore them from the Removed tab if you want them back.`,
      } : undefined,
    );
    setBulkMinProj("");
  };

  // Restore-all for the Removed view — flips `exclude=false` on every
  // player currently in the removed set. Preserves other overrides (locks
  // can't be here by construction; boosts, exposure caps, DT flags stay
  // put). One-click undo for a mis-tuned Min Proj purge.
  const restoreAllRemoved = () => {
    const patches = [];
    // Only undoes YOUR excludes (✕ / Min Proj purge). OUT / IR / bench players
    // stay with the feed — restore those one row at a time (↺) on purpose.
    for (const p of pool) {
      const cur = overrides[String(p.dk_id)] || {};
      if (cur.exclude) patches.push({ dkId: p.dk_id, key: "exclude", value: false }, { dkId: p.dk_id, key: "exclude_reason", value: null });
    }
    if (patches.length === 0) {
      toast.info("No excluded players to restore — OUT / IR / bench rows are held by the injury feed; use ↺ on a row to override it.");
      return;
    }
    patchOverrides(patches);
    // Sprint 60.117 — flip back to Active view after Restore All so users
    // land on the pool they care about instead of staring at an empty
    // Removed tab with a "0" badge.
    setShowRemoved(false);
    toast.success(`Restored ${patches.length / 2} excluded player${patches.length === 2 ? "" : "s"} to the active pool.`, {
      description: "OUT / IR / bench players stay removed — restore those individually with ↺ if you know they play.",
    });
  };

  // Toggle sort direction, or switch column and default to descending
  // (higher-first) since that's what users usually want when scanning
  // projections / ownership / salary.
  const toggleSort = (col) => {
    if (sortBy === col) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(col);
      setSortDir(col === "name" ? "asc" : "desc");
    }
  };

  // Showdown CPT view: re-price every row as the captain slot so the existing
  // table, sorting and value math all work unchanged.
  const slotPool = useMemo(() => {
    if (!showdown || slotView !== "CPT") return pool;
    return pool.map((p) => {
      const m = cptMultOf(p);
      const proj = firstPositive(p.our_proj, p.projection) * m;
      const own = cptOwnOf(p);
      return {
        ...p,
        salary: Math.round(Number(p.salary || 0) * 1.5),
        our_proj: proj,
        projection: proj,
        market_mean_own: own,
        our_own: own,
        ceiling: Number(p.ceiling || 0) > 0 ? Number(p.ceiling) * m : 0,
        _slot: "CPT",
        _cpt_mult: m,
      };
    });
  }, [pool, showdown, slotView]);

  const filtered = useMemo(() => {
    // Removed tab shortcut — bypass the active-pool filters (Leverage
    // Radar, Top Value, game locks) so the user sees EVERYTHING they've
    // excluded regardless of the current active-pool filter state. We
    // still honor position + search for large removed lists.
    if (showRemoved) {
      const removed = pool.filter((p) => !!(overrides[String(p.dk_id)] || {}).exclude || (isOut(p) && !(overrides[String(p.dk_id)] || {}).lock));
      const filteredRemoved = removed
        .filter((p) => posFilter === "ALL" || p.position === posFilter)
        .filter((p) => !query.trim() || (p.name || "").toLowerCase().includes(query.toLowerCase()));
      const accessor = SORT_ACCESSORS[sortBy] || SORT_ACCESSORS.proj;
      const sortedRemoved = [...filteredRemoved].sort((a, b) => {
        const va = accessor(a);
        const vb = accessor(b);
        if (va < vb) return sortDir === "asc" ? -1 : 1;
        if (va > vb) return sortDir === "asc" ? 1 : -1;
        return 0;
      });
      return sortedRemoved.slice(0, 200);
    }

    const baseFiltered = slotPool
      // Hide excluded players from the active view so the Removed tab is
      // the single source of truth for who's out. Users still see the row
      // count in the tab badge; the escape hatch is the Removed tab
      // itself, not a lingering row here with a red ✕ icon.
      .filter((p) => !(overrides[String(p.dk_id)] || {}).exclude)
      .filter((p) => !isOut(p) || (overrides[String(p.dk_id)] || {}).lock)
      // Sprint 60.105 — hide players from unlocked games. Matches
      // what the ILP will exclude at solve-time so the pool view
      // reflects the actual optimizable universe.
      .filter((p) => !p.game_id || !excludedGames.has(p.game_id))
      .filter((p) => posFilter === "ALL" || p.position === posFilter)
      .filter((p) => !query.trim() || (p.name || "").toLowerCase().includes(query.toLowerCase()));

    // Sprint 60.105: also add excludedGames as a dep so the memo
    // recomputes on lock/unlock (used inside baseFiltered above).

    // Leverage Radar: keep only sub-5%-owned players whose projection is
    // in the top quartile of the current filtered set. Computed against
    // the current view so it stays useful after a position filter (e.g.
    // "show me the top-quartile WRs under 5% own").
    let leveraged = baseFiltered;
    if (leverageOn && baseFiltered.length > 0) {
      const projs = baseFiltered
        .map(projOf)
        .filter((v) => v > 0)
        .sort((a, b) => a - b);
      // 75th percentile threshold; if no positive projections exist (rare —
      // deep offseason), fall back to 0 so the ownership gate alone applies.
      const p75 = projs.length ? projs[Math.floor(projs.length * 0.75)] : 0;
      // Sprint 60.107 — accept own < 5 including 0%. A 0%-owned player IS
      // by definition sub-5% and represents peak GPP leverage. The prior
      // `own > 0` gate silently dropped players whose ownership model
      // returned 0 (SGO prop miss / preseason), causing the filter to
      // return empty even on chalk-light slates.
      leveraged = baseFiltered.filter((p) => {
        const own = firstPositive(p.market_mean_own, p.our_own);
        const proj = projOf(p);
        return own < 5 && proj >= p75;
      });
    }

    // Top Value quick filter: keep only players in the top 20% by (proj / salary) × 1000
    // of the currently visible set. Stacks with the position filter — e.g. picking
    // RB + Top Value gives the salary-savers among RBs. Filters an already-Leverage-filtered
    // view too, so power users can compose them.
    if (topValueOn && leveraged.length > 0) {
      const vals = leveraged.map(valueOf).filter((v) => v > 0).sort((a, b) => a - b);
      const p80 = vals.length ? vals[Math.floor(vals.length * 0.80)] : 0;
      // Guard against an all-zero pool (offseason before projections publish).
      if (p80 > 0) leveraged = leveraged.filter((p) => valueOf(p) >= p80);
    }

    const accessor = SORT_ACCESSORS[sortBy] || SORT_ACCESSORS.proj;
    const sorted = [...leveraged].sort((a, b) => {
      const va = accessor(a);
      const vb = accessor(b);
      if (va < vb) return sortDir === "asc" ? -1 : 1;
      if (va > vb) return sortDir === "asc" ? 1 : -1;
      return 0;
    });
    return sorted.slice(0, 100);
  }, [pool, slotPool, posFilter, query, sortBy, sortDir, leverageOn, topValueOn, excludedGames, showRemoved, overrides]);

  const onPoolKeyDown = (e) => {
    if (e.target && ["INPUT", "TEXTAREA", "SELECT"].includes(e.target.tagName)) return;
    const n = filtered.length;
    if (!n) return;
    const cur = focusIdxRef.current;
    const key = e.key;
    if (key === "ArrowDown" || key === "ArrowUp") {
      e.preventDefault();
      const next = key === "ArrowDown" ? Math.min(n - 1, cur + 1) : Math.max(0, cur - 1);
      setFocusIdx(next);
      const row = tableRef.current?.querySelector(`[data-row-idx="${next}"]`);
      row?.scrollIntoView({ block: "nearest" });
      return;
    }
    if (key === "Escape") { setFocusIdx(-1); return; }
    if (cur < 0 || cur >= n) return;
    const p = filtered[cur];
    const ov = overrides[p.dk_id] || {};
    const k = key.toLowerCase();
    if (k === "l") { e.preventDefault(); setField(p.dk_id, "lock", !ov.lock); }
    else if (k === "x") { e.preventDefault(); toggleExclude(p, ov); }
    else if (k === "f") { e.preventDefault(); setField(p.dk_id, "boost_pct", Number(ov.boost_pct) >= 10 ? null : 10); }
    else if (k === "d" && p.position === "QB") { e.preventDefault(); setField(p.dk_id, "dual_threat", !ov.dual_threat); }
  };

  // Count of currently-removed players — powers the tab badge. Uses the
  // full pool (not the filtered view) so the badge stays honest when the
  // user is searching or has a position filter active.
  const removedCount = useMemo(() => {
    let n = 0;
    for (const p of pool) {
      const ov = overrides[String(p.dk_id)] || {};
      if (ov.exclude || (isOut(p) && !ov.lock)) n += 1;
    }
    return n;
  }, [pool, overrides]);

  // Per-position VAL heat-map (QB / RB / WR / TE / DST). For each position we
  // compute the median value of the FULL pool (not the filtered view — the
  // signal is "what's the price-softest position on this slate", which must
  // ignore user filters), plus the pool-wide median for a green/red delta
  // arrow. Clicking a chip drills the pool filter to that position, matching
  // industry-standard grid pattern rendered as a compact strip.
  const positionHeatmap = useMemo(() => {
    if (!pool || pool.length === 0) return null;
    const POSITIONS = ["QB", "RB", "WR", "TE", "DST"];
    const median = (arr) => {
      if (!arr.length) return 0;
      const s = [...arr].sort((a, b) => a - b);
      const m = Math.floor(s.length / 2);
      return s.length % 2 === 0 ? (s[m - 1] + s[m]) / 2 : s[m];
    };
    const allVals = pool.map(valueOf).filter((v) => v > 0);
    const slateMedian = median(allVals);
    const rows = POSITIONS.map((pos) => {
      const posPlayers = pool.filter((p) => p.position === pos);
      const vals = posPlayers.map(valueOf).filter((v) => v > 0);
      const med = median(vals);
      // Best play at this position (highest value, projection > 0).
      let top = null;
      for (const p of posPlayers) {
        const v = valueOf(p);
        if (v <= 0) continue;
        if (!top || v > top.value) top = { name: p.name, value: v };
      }
      return { pos, median: med, count: vals.length, top };
    });
    return { slateMedian, rows };
  }, [pool]);

  const overrideCount = Object.keys(overrides).length;

  return (
    <section className="bg-[#121212] border border-zinc-800">
      <DualThreatGuideModal open={showDtGuide} onClose={() => setShowDtGuide(false)} />
      <div className="px-4 py-3 border-b border-zinc-800 flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Sliders size={16} className="text-[#39FF14]" weight="fill" />
          <div className="heading font-bold">Player Pool Control</div>
          <span className="text-[10px] text-zinc-500 mono uppercase tracking-widest">
            {overrideCount > 0 && (
              <span className="text-[#39FF14]">{overrideCount} active override{overrideCount !== 1 ? "s" : ""}</span>
            )}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setLeverageOn((v) => !v)}
            data-testid="leverage-radar-toggle"
            title="Filter to sub-5%-owned players with top-quartile projection — GPP leverage plays"
            className={`flex items-center gap-1.5 px-2.5 py-1 text-[10px] uppercase tracking-widest border transition-colors ${
              leverageOn
                ? "bg-[#39FF14] text-black border-[#39FF14]"
                : "border-zinc-800 text-zinc-400 hover:border-[#39FF14] hover:text-[#39FF14]"
            }`}
          >
            <Crosshair size={12} weight={leverageOn ? "fill" : "regular"} />
            Leverage Radar
          </button>
          <button
            onClick={() => setTopValueOn((v) => !v)}
            data-testid="top-value-toggle"
            title="Filter to the top 20% by projection-to-price ratio in the current view — cash-game salary savers"
            className={`flex items-center gap-1.5 px-2.5 py-1 text-[10px] uppercase tracking-widest border transition-colors ${
              topValueOn
                ? "bg-[#39FF14] text-black border-[#39FF14]"
                : "border-zinc-800 text-zinc-400 hover:border-[#39FF14] hover:text-[#39FF14]"
            }`}
          >
            <Target size={12} weight={topValueOn ? "fill" : "regular"} />
            Top Value
          </button>
          <button
            onClick={valueAutoTune}
            data-testid="value-auto-tune"
            title="One-click cash-game auto-tune: +15% boost to the top 10% value plays, −15% to the bottom 10%. Composes with any preset. Also satisfies DFS Two-Touch compliance."
            className="flex items-center gap-1.5 px-2.5 py-1 text-[10px] uppercase tracking-widest border border-[#FF8A00]/50 text-[#FF8A00] hover:bg-[#FF8A00] hover:text-black transition-colors font-bold"
          >
            <Sparkle size={12} weight="fill" />
            Value Auto-Tune
          </button>
          <a
            href={api.poolExportURL()}
            data-testid="pool-export-csv"
            title="Download the current player pool as a CSV — Player · Pos · Team · Salary · Projection · Value · Ownership. Research/audit file, not a DK-upload lineup CSV."
            className="flex items-center gap-1.5 px-2.5 py-1 text-[10px] uppercase tracking-widest border border-zinc-800 text-zinc-400 hover:border-[#39FF14] hover:text-[#39FF14] transition-colors"
          >
            <DownloadSimple size={12} />
            Pool CSV
          </a>
          <div className="flex gap-1" data-testid="pool-pos-filters">
            {["ALL", "QB", "RB", "WR", "TE", ...(showdown ? ["K"] : []), "DST"].map((p) => (
              <button key={p} onClick={() => setPosFilter(p)} data-testid={`pool-pos-${p.toLowerCase()}`}
                className={`px-2.5 py-1 text-[10px] uppercase border transition-colors ${
                  posFilter === p ? "bg-[#39FF14] text-black border-[#39FF14]"
                                  : "border-zinc-800 text-zinc-400 hover:border-zinc-600"
                }`}>{p}</button>
            ))}
          </div>
          {showdown && (
            <div className="flex gap-1" data-testid="pool-slot-view"
                 title="Showdown has two different prices for the same player. FLEX = 1x salary and flex ownership. CPT = 1.5x salary, the captain projection and captain ownership. Sorting follows the view.">
              {["FLEX", "CPT"].map((s) => (
                <button key={s} onClick={() => setSlotView(s)} data-testid={`pool-slot-${s.toLowerCase()}`}
                  aria-pressed={slotView === s}
                  className={`px-2.5 py-1 text-[10px] uppercase border transition-colors inline-flex items-center gap-1 ${
                    slotView === s
                      ? (s === "CPT" ? "bg-[#FF3B30] text-black border-[#FF3B30] font-bold" : "bg-[#FFCC00] text-black border-[#FFCC00] font-bold")
                      : "border-zinc-800 text-zinc-400 hover:border-zinc-600"
                  }`}>
                  {s === "CPT" && <Crown size={10} weight="fill" />}{s}
                </button>
              ))}
            </div>
          )}
          {/* Reset Pool — always visible so users never wonder if it
              was removed. Greyed / disabled when there are no overrides
              to reset; full red destructive-action styling when there's
              actual state to wipe. */}
          <button
            onClick={clearAll}
            data-testid="pool-reset-btn"
            disabled={overrideCount === 0}
            title={overrideCount > 0
              ? "Reset Player Pool — clears every user override (locks, excludes, DT flags, min/max exposure, boosts, projection edits) and restores every player to the model's original state."
              : "No overrides to reset. This button lights up red once you lock, exclude, boost, or cap any player."}
            className={`flex items-center gap-1.5 px-2.5 py-1 text-[10px] uppercase tracking-widest border transition-colors font-bold ${
              overrideCount > 0
                ? "border-[#FF3B30]/50 text-[#FF3B30] hover:bg-[#FF3B30] hover:text-black"
                : "border-zinc-800 text-zinc-600 cursor-not-allowed opacity-60"
            }`}
          >
            <ArrowCounterClockwise size={12} weight="bold" />
            Reset Pool
          </button>
        </div>
      </div>

      <ChalkWatchWidget variant="slim" limit={3} />

      {positionHeatmap && positionHeatmap.slateMedian > 0 && (
        <div
          className="px-4 py-2.5 border-b border-zinc-800 bg-black/40"
          data-testid="position-value-heatmap"
        >
          <div className="flex items-center gap-2 mb-1.5">
            <span className="mono text-[9px] uppercase tracking-widest text-zinc-500">
              Value Heat Map · median VAL by position
            </span>
            <span className="mono text-[9px] text-zinc-600">
              slate median {positionHeatmap.slateMedian.toFixed(2)}x
            </span>
          </div>
          <div className="grid grid-cols-5 gap-1.5">
            {positionHeatmap.rows.map((r) => {
              const delta = r.median - positionHeatmap.slateMedian;
              // Signal band: within 0.10x of slate median = neutral; above = soft-priced
              // position (cash-game salary saver); below = expensive position, chalk-heavy.
              const softer = delta > 0.10;
              const harder = delta < -0.10;
              const active = posFilter === r.pos;
              const bandCls = softer
                ? "border-[#39FF14]/50 text-[#39FF14]"
                : harder
                  ? "border-[#ef4444]/40 text-[#ef4444]"
                  : "border-zinc-800 text-zinc-300";
              const activeCls = active
                ? "bg-[#39FF14] text-black border-[#39FF14]"
                : bandCls;
              return (
                <button
                  key={r.pos}
                  type="button"
                  onClick={() => setPosFilter((cur) => (cur === r.pos ? "ALL" : r.pos))}
                  data-testid={`heatmap-${r.pos}`}
                  title={
                    r.top
                      ? `${r.pos} median ${r.median.toFixed(2)}x (${r.count} plays) · best: ${r.top.name} ${r.top.value.toFixed(2)}x · ${softer ? "PRICED SOFT" : harder ? "PRICED HARD" : "at slate median"}. Click to filter.`
                      : `${r.pos} — no projections yet`
                  }
                  className={`flex flex-col items-start gap-0.5 px-2 py-1.5 border transition-colors hover:border-[#39FF14] ${activeCls}`}
                >
                  <div className="flex items-center gap-1.5 w-full">
                    <span className="mono text-[10px] font-bold uppercase tracking-widest">{r.pos}</span>
                    {softer && !active && <CaretUp size={9} weight="fill" className="text-[#39FF14]" />}
                    {harder && !active && <CaretDown size={9} weight="fill" className="text-[#ef4444]" />}
                    <span className="mono text-[11px] font-bold ml-auto">
                      {r.median > 0 ? `${r.median.toFixed(2)}x` : "—"}
                    </span>
                  </div>
                  <div className={`mono text-[9px] truncate w-full ${active ? "text-black/70" : "text-zinc-500"}`}>
                    {r.top ? `${r.top.name.split(" ").slice(-1)[0]} ${r.top.value.toFixed(2)}x` : `${r.count} plays`}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      <div className="px-4 py-2 border-b border-zinc-800">
        <div className="relative">
          <MagnifyingGlass size={12} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
          <input value={query} onChange={(e) => setQuery(e.target.value)}
            placeholder="Search player…"
            data-testid="player-pool-search"
            className="w-full bg-black border border-zinc-800 focus:border-[#39FF14] outline-none pl-9 pr-3 py-1.5 text-xs" />
        </div>
      </div>

      {/*
        Bulk Exposure Toolbar — apply Min/Max caps to the WHOLE current
        position scope in one action. Applied to the full pool (not the
        row-cap-truncated view). Scope defaults to ALL when no position
        filter is active; otherwise scoped to that position. Empty inputs
        are a no-op for that column so the user can set only Min or only Max
        without wiping the other. See applyBulkExposure() for validation.
      */}
      <div
        className={`px-4 py-2 border-b flex items-center gap-2 flex-wrap transition-colors ${
          bulkAllPool
            ? "bg-[#FF8A00]/10 border-[#FF8A00]/40"
            : "bg-black/30 border-zinc-800"
        }`}
        data-testid="bulk-exposure-toolbar"
      >
        {bulkAllPool && (
          <span
            className="shrink-0 inline-flex items-center gap-1 text-[#FF8A00]"
            title="The Entire Pool override is active — every bulk apply/clear hits every position."
          >
            <span className="w-1.5 h-1.5 rounded-full bg-[#FF8A00] animate-pulse" />
            <span className="mono text-[9px] uppercase tracking-widest font-bold">Override</span>
            <span className="text-zinc-500 mono text-[9px]">·</span>
          </span>
        )}
        <span className="mono text-[9px] uppercase tracking-widest text-zinc-500 shrink-0">
          Bulk exposure ·
        </span>
        <span
          className="mono text-[10px] uppercase tracking-widest text-white font-bold shrink-0"
          data-testid="bulk-exposure-scope"
        >
          {bulkAllPool
            ? "Entire pool"
            : (posFilter === "ALL" ? "All players" : `All ${posFilter}s`)}
        </span>
        <span className="mono text-[9px] text-zinc-600 shrink-0">
          ({pool.filter((p) => (bulkAllPool || posFilter === "ALL") || p.position === posFilter).length})
        </span>
        {/* Entire-pool toggle — decouples the bulk-edit scope from the top
            table filter so users can visually filter by QB while still
            capping exposure on every position in one shot. */}
        <button
          type="button"
          data-testid="bulk-scope-all-pool"
          onClick={() => setBulkAllPool((v) => !v)}
          aria-pressed={bulkAllPool}
          title={bulkAllPool
            ? "Currently bulk-editing every position. Auto-clears the next time you switch position filters at the top. Amber styling because this override ignores your position filter — it's meant to be temporary."
            : "One-shot override — bulk-edit every position at once, ignoring the top table's position filter. Auto-clears when you switch positions. Uses amber styling so you never forget it's on."}
          className={`shrink-0 inline-flex items-center gap-1 px-2 py-1 text-[9px] mono uppercase tracking-widest border transition-all duration-150 active:scale-95 ${
            bulkAllPool
              ? "bg-gradient-to-b from-[#FF8A00] to-[#e07a00] text-black border-[#FF8A00] shadow-[0_0_8px_-2px_rgba(255,138,0,0.6)] font-bold"
              : "bg-black border-zinc-800 text-zinc-400 hover:border-[#FF8A00] hover:text-[#FF8A00]"
          }`}
        >
          <span className={`w-1.5 h-1.5 rounded-full ${bulkAllPool ? "bg-black" : "bg-zinc-600"}`} />
          Entire pool
        </button>
        <div className="flex items-center gap-1.5 ml-auto flex-wrap">
          <label className="mono text-[10px] uppercase tracking-widest text-zinc-500 flex items-center gap-1">
            Min
            <input
              type="number"
              min="0"
              max="100"
              step="5"
              value={bulkMin}
              onChange={(e) => setBulkMin(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && applyBulkExposure()}
              placeholder="—"
              data-testid="bulk-min-input"
              className="w-14 bg-black border border-zinc-800 focus:border-[#39FF14] outline-none px-2 py-1 text-xs mono text-white"
            />
            <span className="text-zinc-600">%</span>
          </label>
          <label className="mono text-[10px] uppercase tracking-widest text-zinc-500 flex items-center gap-1">
            Max
            <input
              type="number"
              min="0"
              max="100"
              step="5"
              value={bulkMax}
              onChange={(e) => setBulkMax(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && applyBulkExposure()}
              placeholder="—"
              data-testid="bulk-max-input"
              className="w-14 bg-black border border-zinc-800 focus:border-[#39FF14] outline-none px-2 py-1 text-xs mono text-white"
            />
            <span className="text-zinc-600">%</span>
          </label>
          {/* Min Proj purge — quick way to strip backups (2-11 pt projections)
              off preseason / Week 1 slates. Writes exclude=true; the removed
              players show up in the Removed tab where they can be restored. */}
          <label
            className="mono text-[10px] uppercase tracking-widest text-zinc-500 flex items-center gap-1"
            title="Remove every player in scope whose projection is below this value. Locked players are skipped. Removed players land in the Removed tab and can be restored."
          >
            Min Proj
            <input
              type="number"
              min="0"
              step="1"
              value={bulkMinProj}
              onChange={(e) => setBulkMinProj(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && applyBulkMinProj()}
              placeholder="—"
              data-testid="bulk-min-proj-input"
              className="w-14 bg-black border border-zinc-800 focus:border-[#FF3B30] outline-none px-2 py-1 text-xs mono text-white"
            />
            <span className="text-zinc-600">pts</span>
          </label>
          <button
            onClick={applyBulkMinProj}
            data-testid="bulk-min-proj-apply"
            title={`Remove every ${(bulkAllPool || posFilter === "ALL") ? "projected player" : posFilter} below the Min Proj threshold. Locked rows are skipped. Undo via the Removed tab.`}
            className="px-3 py-1 border border-[#FF3B30]/60 text-[#FF3B30] hover:bg-[#FF3B30] hover:text-black uppercase tracking-widest text-[10px] font-bold mono transition-colors"
          >
            Purge
          </button>
          <button
            onClick={applyBulkExposure}
            data-testid="bulk-apply"
            title={`Apply Min/Max exposure to ${(bulkAllPool || posFilter === "ALL") ? "every projected player" : `every ${posFilter}`} in the pool. Empty inputs are ignored.`}
            className="px-3 py-1 bg-[#39FF14] text-black hover:bg-[#2ee30d] uppercase tracking-widest text-[10px] font-bold mono transition-colors"
          >
            Apply to {bulkAllPool ? "Pool" : (posFilter === "ALL" ? "All" : posFilter)}
          </button>
          {/* Sprint 60.141 — Refresh Projections button. Re-runs
              `_rebuild_derived` server-side so this pool picks up the
              latest admin override + latest SGO cache. No SGO wire
              hit; safe to spam pre-lock. Does NOT reset admin
              overrides — those only change on a new admin push. */}
          <button
            data-testid="pool-refresh-projections"
            onClick={async () => {
              try {
                const r = await api.refreshPoolProjections();
                const overr = r.proj_overrides_applied || 0;
                const own = r.own_overrides_applied || 0;
                const tail = (overr || own)
                  ? ` · Admin overrides live: ${overr} proj / ${own} own`
                  : "";
                toast.success(`Refreshed ${r.pool_size} players${tail}`, { duration: 6000 });
                window.dispatchEvent(new CustomEvent("wp:pool-refreshed"));
              } catch (e) {
                toast.error(e?.response?.data?.detail || "Refresh failed");
              }
            }}
            title="Pull the latest projections + ownership from the WarPaint model. If the admin pushed a hard override in the last few minutes, this button ensures you're building against it — not the previous stale set."
            className="px-3 py-1 border border-[#00B4FF]/60 text-[#00B4FF] hover:bg-[#00B4FF] hover:text-black uppercase tracking-widest text-[10px] font-bold mono transition-colors"
          >
            ⟳ Refresh Projections
          </button>
          <button
            onClick={clearBulkExposure}
            data-testid="bulk-clear"
            title={`Remove Min/Max exposure caps from ${(bulkAllPool || posFilter === "ALL") ? "every player" : `every ${posFilter}`}. Locks, excludes, boosts, and proj overrides are preserved.`}
            className="px-3 py-1 border border-zinc-800 hover:border-[#FF3B30] text-zinc-400 hover:text-[#FF3B30] uppercase tracking-widest text-[10px] font-bold mono transition-colors"
          >
            Clear
          </button>
        </div>
      </div>

      {/* "Gotcha" warning strip — only shows when the user has the Entire Pool
          override on AND is viewing a specific position at the top. Without
          this, users click into RB, type a Max Exp, hit Apply, and are
          confused when it clobbers every position in the pool. Now they see
          exactly what's about to happen, why, and how to switch it off. */}
      {bulkAllPool && posFilter !== "ALL" && (
        <div
          data-testid="bulk-scope-override-warning"
          className="px-4 py-2 border-b border-[#FF8A00]/40 bg-[#FF8A00]/10 flex items-center gap-3 flex-wrap"
        >
          <span className="mono text-[9px] uppercase tracking-widest text-[#FF8A00] font-bold shrink-0">
            Heads up
          </span>
          <span className="text-xs text-zinc-200 leading-tight">
            You're viewing <span className="text-[#FF8A00] font-bold">{posFilter}s</span> at the top, but the{" "}
            <span className="text-[#FF8A00] font-bold">Entire Pool</span> override is on — the next{" "}
            <span className="text-white font-bold">Apply</span> will hit <span className="text-[#FF8A00] font-bold">every position</span>, not just {posFilter}s.
          </span>
          <button
            type="button"
            data-testid="bulk-scope-override-off"
            onClick={() => setBulkAllPool(false)}
            className="ml-auto shrink-0 inline-flex items-center gap-1 border border-[#FF8A00] hover:bg-[#FF8A00] hover:text-black text-[#FF8A00] px-2 py-1 text-[10px] mono uppercase tracking-widest font-bold transition-colors"
          >
            Scope back to {posFilter}s
          </button>
        </div>
      )}

      <DualThreatWeeklyBanner overrides={overrides} pool={pool} onOpenGuide={() => setShowDtGuide(true)} />

      {/* Icon legend — quick cheatsheet for first-time users. LS-dismissible.
          Reads left-to-right in the exact order the columns appear so users
          can visually anchor each icon to its column below. */}
      {showIconLegend && (
        <div
          data-testid="pool-icon-legend"
          className="px-4 py-2 border-b border-zinc-800 bg-black/50 flex items-center gap-4 flex-wrap"
        >
          <span className="mono text-[9px] uppercase tracking-widest text-zinc-500 shrink-0">
            Icon guide
          </span>
          <div className="flex items-center gap-1.5" title="Lock — always include this player in every generated lineup">
            <span className="w-5 h-5 rounded-md inline-flex items-center justify-center bg-gradient-to-b from-[#39FF14] to-[#2ee30d] text-black shadow-[0_0_6px_-2px_rgba(57,255,20,0.6)]">
              <Lock size={10} weight="fill" />
            </span>
            <span className="text-[10px] text-zinc-300"><span className="text-[#39FF14] font-bold uppercase tracking-widest mono">Lock</span> · always in</span>
          </div>
          <div className="flex items-center gap-1.5" title="Exclude — never include this player in any lineup">
            <span className="w-5 h-5 rounded-md inline-flex items-center justify-center bg-gradient-to-b from-[#FF3B30] to-[#d92e24] text-white shadow-[0_0_6px_-2px_rgba(255,59,48,0.6)]">
              <Prohibit size={10} weight="fill" />
            </span>
            <span className="text-[10px] text-zinc-300"><span className="text-[#FF3B30] font-bold uppercase tracking-widest mono">Exclude</span> · never in</span>
          </div>
          <div className="flex items-center gap-1.5" title="Dual-Threat — mark rushing-heavy QBs (Josh Allen / Lamar / Hurts / Fields tier) so the optimizer can use them for naked-QB stacks">
            <span className="w-5 h-5 rounded-md inline-flex items-center justify-center bg-gradient-to-b from-[#FF8A00] to-[#e07a00] text-black shadow-[0_0_6px_-2px_rgba(255,138,0,0.6)]">
              <TrendUp size={10} weight="fill" />
            </span>
            <span className="text-[10px] text-zinc-300"><span className="text-[#FF8A00] font-bold uppercase tracking-widest mono">DT</span> · dual-threat QB (for naked-QB stacks)</span>
          </div>
          <button
            type="button"
            data-testid="pool-icon-legend-dismiss"
            onClick={dismissIconLegend}
            className="ml-auto text-[9px] mono uppercase tracking-widest text-zinc-500 hover:text-[#39FF14] transition-colors"
            title="Hide this guide (you can hover any icon for a tooltip)"
          >
            Got it · hide
          </button>
        </div>
      )}

      {/* Active / Removed tab strip — prominent placement OUTSIDE the
          scroll container so users always see it without scrolling past
          the DT banner + icon legend + heatmap. Bigger, chunkier styling
          than an inline segmented control so the "Removed" affordance
          reads as a full-width tab bar, not a secondary chip. */}
      <div
        className="px-4 pt-3 pb-2 border-b border-zinc-800 flex items-center gap-2 flex-wrap"
        data-testid="pool-tab-strip"
      >
        <span className="mono text-[9px] uppercase tracking-widest text-zinc-500 shrink-0">
          View ·
        </span>
        <button
          type="button"
          onClick={() => setShowRemoved(false)}
          data-testid="pool-tab-active"
          aria-pressed={!showRemoved}
          title="The active player pool — every player available to the optimizer."
          className={`inline-flex items-center gap-1.5 px-3 py-1.5 mono text-[11px] uppercase tracking-widest border-2 transition-colors ${
            !showRemoved
              ? "bg-[#39FF14] text-black border-[#39FF14] font-bold shadow-[0_0_10px_-3px_rgba(57,255,20,0.55)]"
              : "bg-black border-zinc-800 text-zinc-400 hover:border-[#39FF14] hover:text-[#39FF14]"
          }`}
        >
          Active Pool
        </button>
        <button
          type="button"
          onClick={() => setShowRemoved(true)}
          data-testid="pool-tab-removed"
          aria-pressed={showRemoved}
          title="Players you've excluded manually (✕ button) or via the Min Proj purge. Click here to review and restore any of them."
          className={`inline-flex items-center gap-1.5 px-3 py-1.5 mono text-[11px] uppercase tracking-widest border-2 transition-colors ${
            showRemoved
              ? "bg-[#FF3B30] text-white border-[#FF3B30] font-bold shadow-[0_0_10px_-3px_rgba(255,59,48,0.55)]"
              : "bg-black border-zinc-800 text-zinc-400 hover:border-[#FF3B30] hover:text-[#FF3B30]"
          }`}
        >
          Removed
          <span
            data-testid="pool-tab-removed-badge"
            className={`ml-1 px-1.5 py-0 border font-bold text-[10px] ${
              showRemoved
                ? "bg-white text-[#FF3B30] border-white"
                : removedCount > 0
                  ? "bg-[#FF3B30]/25 text-[#FF3B30] border-[#FF3B30]/60"
                  : "bg-black text-zinc-600 border-zinc-800"
            }`}
          >
            {removedCount}
          </span>
        </button>
        {showRemoved && removedCount > 0 && (
          <button
            type="button"
            onClick={restoreAllRemoved}
            data-testid="pool-restore-all"
            title="Undo every ✕ / Min Proj exclude. OUT / IR / bench players stay with the injury feed (restore those one row at a time). Locks, boosts, and exposure caps are preserved."
            className="ml-auto inline-flex items-center gap-1.5 px-3 py-1.5 mono text-[11px] uppercase tracking-widest border-2 border-[#39FF14]/60 text-[#39FF14] hover:bg-[#39FF14] hover:text-black transition-colors font-bold"
          >
            <ArrowCounterClockwise size={12} weight="bold" />
            Restore All ({removedCount})
          </button>
        )}
        {showRemoved && (
          <span className="ml-2 mono text-[10px] text-zinc-500">
            Removed = players the optimizer will never roster. The tag on each row says why: <span className="text-[#FF3B30]">MY EXCLUDE</span> (your ✕), <span className="text-[#FF8A00]">PURGED</span> (Min Proj sweep), OUT / IR / D (injury feed), BENCH (backup QB), UNROSTERED (practice squad / cut). Click ↺ to restore a row and set your own projection.
          </span>
        )}
        {!showRemoved && (
          <span
            className="ml-auto hidden md:inline-flex items-center gap-1.5 mono text-[9px] uppercase tracking-widest text-zinc-500"
            data-testid="pool-keyboard-hint"
            title="Click a row (or press ↓) to move the cursor, then: L = lock · X = exclude · F = like (+10%) · D = dual-threat (QB) · Esc = clear"
          >
            <Keyboard size={12} className="text-[#39FF14]/70" />
            ↑↓ · L lock · X exclude · F like
          </span>
        )}
      </div>

      <div
        ref={tableRef}
        tabIndex={0}
        onKeyDown={onPoolKeyDown}
        data-testid="pool-table-container"
        className="max-h-[520px] overflow-y-auto overflow-x-auto outline-none focus:ring-1 focus:ring-inset focus:ring-[#39FF14]/40"
      >
        <table className="w-full min-w-[1080px] text-xs mono">
          <thead className="bg-black sticky top-0 z-10">
            <tr className="text-left text-zinc-400 uppercase tracking-widest text-[10px]">
              <SortableTh label="Player" col="name" sortBy={sortBy} sortDir={sortDir} onClick={toggleSort} />
              <SortableTh label={showdown && slotView === "CPT" ? "CPT Sal" : "Sal"} col="salary" sortBy={sortBy} sortDir={sortDir} onClick={toggleSort} />
              <SortableTh label={showdown && slotView === "CPT" ? "CPT Proj" : "Proj"} col="proj" sortBy={sortBy} sortDir={sortDir} onClick={toggleSort} title="Model projection — click to sort" />
              <SortableTh label="Val" col="value" sortBy={sortBy} sortDir={sortDir} onClick={toggleSort} title="Value = (Projection ÷ Salary) × 1000. ≥ 3.0 is elite salary savings. Click to sort." />
              <SortableTh label={showdown && slotView === "CPT" ? "CPT Own" : (showdown ? "FLEX Own" : "Own")} col="own" sortBy={sortBy} sortDir={sortDir} onClick={toggleSort} title="Consensus ownership % — click to sort (low-owned first when ascending)" />
              <SortableTh label="Ceil" col="ceiling" sortBy={sortBy} sortDir={sortDir} onClick={toggleSort} title="90th-percentile ceiling from the correlated simulator (position volatility × projection). Click to sort." />
              <SortableTh label="Lev" col="leverage" sortBy={sortBy} sortDir={sortDir} onClick={toggleSort} title="Leverage = Ceiling ÷ Ownership. ≥ 4.0 = elite tournament leverage (big ceiling the field isn't on). Click to sort." />
              <SortableTh label="Lev Score" col="levscore" sortBy={sortBy} sortDir={sortDir} onClick={toggleSort} title="Leverage Score = our sim Optimal% − the field's REAL ownership (recounted from imported field lineups when loaded), scaled for duplication risk. +10 or more = elite leverage, −10 or worse = chalk trap. Click to sort." />
              <SortableTh label="Mkt Δ" col="mkt" sortBy={sortBy} sortDir={sortDir} onClick={toggleSort} title="Our projection minus the market consensus projection. Green = we're higher than the field's number; red = lower. '—' when no market line is available." />
              <th className="px-3 py-2 text-center" title="Lock into every lineup">
                <Lock size={12} weight="fill" className="inline text-[#39FF14]/70" />
              </th>
              <th className="px-3 py-2 text-center" title="Exclude from all lineups">
                <Prohibit size={12} weight="fill" className="inline text-[#FF3B30]/70" />
              </th>
              <th className="px-3 py-2 text-center" title="Like — one-click +10% projection boost (soft lean without a hard lock)">
                <Star size={12} weight="fill" className="inline text-[#FFCC00]/70" />
              </th>
              <th
                className="px-3 py-2 text-center"
                title="Dual-Threat QB flag — check this each week for QBs whose fantasy production is rushing-heavy (Josh Allen, Lamar, Hurts, Fields tier). Only DT-flagged QBs can be used for `primary_only` (naked QB, no receivers) stacks. Blank for non-QBs."
                data-testid="dt-column-header"
              >
                <span className="inline-flex items-center gap-1 text-[#FF8A00]">
                  <TrendUp size={12} weight="fill" />
                  <span className="text-[9px] tracking-widest">DT</span>
                </span>
              </th>
              <th className="px-3 py-2" title="Projection boost % (adds to Proj)">Boost</th>
              <th className="px-3 py-2" title="Minimum exposure % — floor how often this player appears">Min Exp</th>
              <th className="px-3 py-2" title="Maximum exposure % — cap how often this player appears">Max Exp</th>
              {showdown && (
                <>
                  <th className="px-3 py-2 text-center" title="Pin as Captain — this player wears the crown (1.5× salary & points). Pin several to rotate the crown among them." data-testid="cpt-column-header">
                    <span className="inline-flex items-center gap-1 text-[#FF3B30]"><Crown size={12} weight="fill" /><span className="text-[9px] tracking-widest">CPT</span></span>
                  </th>
                  <th className="px-3 py-2 text-center" title="Never Captain — can still be rostered at FLEX">No CPT</th>
                  <th className="px-3 py-2" title="Captain exposure floor % — force this player to wear the crown in at least this share of lineups (all CPT MIN values must total ≤ 100%)" data-testid="cpt-min-column-header">CPT Min</th>
                  <th className="px-3 py-2" title="Captain exposure cap % — how often this player may wear the crown">CPT Max</th>
                </>
              )}
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={16} className="px-3 py-4 text-center text-zinc-500">Loading pool…</td></tr>
            )}
            {!loading && filtered.length === 0 && (
              <tr><td colSpan={16} className="px-3 py-6 text-center text-zinc-500">
                {showRemoved ? (
                  removedCount === 0
                    ? "No removed players yet. Use Min Proj purge or the ✕ button to drop players out of the pool."
                    : "No removed players match the current search / position filter."
                ) : (topValueOn || leverageOn || posFilter !== "ALL" || query.trim()) ? (
                  <div className="flex flex-col items-center gap-3">
                    <span>
                      {topValueOn && leverageOn
                        ? "No plays match both Leverage Radar AND Top Value in this view."
                        : topValueOn
                          ? "No top-20%-value plays in this view."
                          : leverageOn
                            ? "No sub-5%-own top-quartile plays in this view."
                            : "No players match the current filter."}
                    </span>
                    <button
                      type="button"
                      onClick={clearFilters}
                      data-testid="pool-clear-filters-btn"
                      className="px-3 py-1.5 text-[10px] mono uppercase tracking-widest border border-[#39FF14]/60 text-[#39FF14] hover:bg-[#39FF14] hover:text-black transition-colors font-bold"
                    >
                      Clear filters · restore pool
                    </button>
                  </div>
                ) : "Upload DK pool first."}
              </td></tr>
            )}
            {filtered.map((p, rowIdx) => {
              const ov = overrides[p.dk_id] || {};
              const highlight = Object.keys(ov).length > 0;
              const isFocusedRow = rowIdx === focusIdx;
              // Model-projected value — fall back through the pool's known projection
              // columns; during offseason (all projection cols zero) we further fall back
              // to season avg PPG so users still see a meaningful number to edit.
              const baseProj = isOut(p) ? 0 : firstPositive(p.our_proj, p.projection, p.market_mean_proj, p.avg_ppg);
              // Value cell reacts to the user's projection override so tweaking
              // Proj updates Val in real time — critical for salary-cap tuning.
              const effectiveProj = firstPositive(ov.proj_override, baseProj);
              const salaryNum = Number(p.salary || 0);
              const valueNum = salaryNum > 0 ? (effectiveProj / salaryNum) * 1000 : 0;
              const flagKey = String(p.dk_id || p.name || "");
              const isRedFlagged = flagKey && redFlagged.has(flagKey);
              return (
                <React.Fragment key={p.dk_id}>
                <tr
                  data-row-idx={rowIdx}
                  data-testid={`pool-row-${p.dk_id}`}
                  onClick={() => setFocusIdx(rowIdx)}
                  className={`border-t border-zinc-900 ${highlight ? "bg-[#39FF14]/5" : ""} ${isFocusedRow ? "outline outline-1 outline-[#39FF14]/70 bg-zinc-900/60" : ""}`}
                >
                  <td className="px-3 py-2.5 min-w-[190px]">
                    <div className="font-medium flex items-center gap-1.5 relative whitespace-nowrap">
                      {isRedFlagged && (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setPivotFor((cur) => (cur === p.dk_id ? null : p.dk_id));
                          }}
                          title="High chalk-alert — click for lower-owned pivot options at similar salary/position."
                          data-testid={`chalk-flag-${p.dk_id}`}
                          className="inline-flex items-center hover:scale-110 transition-transform"
                          aria-label="High chalk-alert — likely duplication risk"
                        >
                          <Warning size={11} weight="fill" className="text-[#ef4444] shrink-0" />
                        </button>
                      )}
                      <PlayerHeadshot player={p} sleeperId={headshots[String(p.dk_id)]} size={30} />
                      <span>{p.name}</span>
                      {p.on_bye && (
                        <span
                          data-testid={`bye-badge-${p.dk_id}`}
                          title={`${p.team} is on BYE this week — locked lineups will be infeasible.`}
                          className="ml-1 px-1 py-[1px] text-[8px] font-bold mono bg-amber-500/25 text-amber-300 border border-amber-500/60 tracking-wide"
                        >
                          BYE
                        </span>
                      )}
                      {/* Sprint 60.98 — Admin-only OVR badge. Renders when
                          the current user is is_admin AND this player row
                          has an active projection or ownership override.
                          Regular users never see this even if the flag
                          arrives in the payload. */}
                      {isAdmin && (p.admin_proj_overridden || p.admin_own_overridden) && (
                        <span
                          data-testid={`admin-override-badge-${p.dk_id}`}
                          title={[
                            p.admin_proj_overridden && "projection override active (50/50 blend)",
                            p.admin_own_overridden && "ownership override active (50/50 blend)",
                          ].filter(Boolean).join(" · ")}
                          className="ml-1 px-1 py-[1px] text-[8px] font-bold mono bg-[#39FF14]/20 text-[#39FF14] border border-[#39FF14]/60 tracking-wide"
                        >
                          OVR{p.admin_proj_overridden && p.admin_own_overridden ? "·2" : ""}
                        </span>
                      )}
                      {p.is_injured && p.injury_status && (
                        <span
                          data-testid={`inj-badge-${p.dk_id}`}
                          title={`Injury status: ${p.injury_status}${p.prev_week_status ? ` · last wk: ${p.prev_week_status} (expired, DK has not carried it into this slate)` : ""}`}
                          className={`ml-1 px-1 py-[1px] text-[8px] font-bold mono border tracking-wide ${
                            String(p.injury_status).toUpperCase().startsWith("O") ||
                            String(p.injury_status).toUpperCase().includes("OUT")
                              ? "bg-red-500/25 text-red-300 border-red-500/60"
                              : String(p.injury_status).toUpperCase().startsWith("D")
                              ? "bg-red-500/20 text-red-400 border-red-500/50"
                              : "bg-amber-500/20 text-amber-300 border-amber-500/50"
                          }`}
                        >
                          {String(p.injury_status).slice(0, 3).toUpperCase()}
                        </span>
                      )}
                      {!p.injury_status && p.prev_week_status && (
                        <span
                          data-testid={`stale-inj-badge-${p.dk_id}`}
                          title={`Last week: ${p.prev_week_status} — that ruling expired with last slate and DK has not tagged ${p.name} for this one`}
                          className="ml-1 px-1 py-[1px] text-[8px] font-bold mono border tracking-wide text-zinc-500 border-zinc-700"
                        >
                          LW {String(p.prev_week_status).slice(0, 3).toUpperCase()}
                        </span>
                      )}
                      {showRemoved && ov.exclude && (
                        <span data-testid={`removed-reason-${p.dk_id}`}
                              title={String(ov.exclude_reason || "").startsWith("purge") ? `Swept by your Min Proj purge (${ov.exclude_reason.replace("purge", "proj ")})` : "You excluded this player with the ✕ button"}
                              className={`ml-1 px-1 py-[1px] text-[8px] font-bold mono border tracking-wide ${String(ov.exclude_reason || "").startsWith("purge") ? "text-[#FF8A00] border-[#FF8A00]/50" : "text-[#FF3B30] border-[#FF3B30]/50"}`}>
                          {String(ov.exclude_reason || "").startsWith("purge") ? "PURGED" : "MY EXCLUDE"}
                        </span>
                      )}
                      {showRemoved && !ov.exclude && !p.injury_status && p.unplayable_reason && p.unplayable_reason !== "BACKUP_QB" && (
                        <span data-testid={`removed-reason-${p.dk_id}`} title="Removed by the data feed — not on a 53-man roster or flagged unplayable"
                              className="ml-1 px-1 py-[1px] text-[8px] font-bold mono border tracking-wide text-zinc-400 border-zinc-600">
                          {String(p.unplayable_reason).replace("_", " ")}
                        </span>
                      )}
                      {!p.injury_status && p.unplayable_reason === "BACKUP_QB" && (
                        <span data-testid={`bench-badge-${p.dk_id}`} title="Backup QB — not the starter, projects 0 and sits in Removed (restore if you know otherwise)"
                              className="ml-1 px-1 py-[1px] text-[8px] font-bold mono border tracking-wide bg-zinc-800 text-zinc-400 border-zinc-600">
                          BENCH
                        </span>
                      )}
                      {ov.restore && (
                        <button
                          type="button"
                          data-testid={`restored-badge-${p.dk_id}`}
                          onClick={() => setField(p.dk_id, "restore", false)}
                          title="You restored this player over the injury feed. Click to hand him back to the feed (removes him again)."
                          className="ml-1 px-1 py-[1px] text-[8px] font-bold mono border tracking-wide bg-[#39FF14]/15 text-[#39FF14] border-[#39FF14]/50 hover:bg-[#FF3B30]/20 hover:text-[#FF3B30] hover:border-[#FF3B30]/50"
                        >
                          RESTORED
                        </button>
                      )}
                      {(ov.restore || ov.lock) && projOf(p) <= 0 && (
                        <span data-testid={`zero-proj-badge-${p.dk_id}`}
                              title="No projection anywhere (model, market, season avg) — the optimizer drops 0.0 players even when locked or restored. Type a projection in the PROJ box to keep him."
                              className="ml-1 px-1 py-[1px] text-[8px] font-bold mono border tracking-wide text-[#FF3B30] border-[#FF3B30]/50">
                          ZERO PROJ · SET ONE
                        </span>
                      )}
                      {pivotFor === p.dk_id && (
                        <PivotSuggestionsPopover
                          chalkId={p.dk_id}
                          chalkName={p.name}
                          chalkBaseProj={baseProj}
                          overrides={overrides}
                          patchOverrides={patchOverrides}
                          onClose={() => setPivotFor(null)}
                        />
                      )}
                    </div>
                    <div className="text-[10px] text-zinc-500 uppercase">{p.position} · <span className="font-black" style={{ color: teamTextColor(p.team) }}>{p.team}</span></div>
                  </td>
                  <td className="px-3 py-2.5">${p.salary}</td>
                  <td className="px-3 py-2.5">
                    <input
                      type="number" step="0.5" min="0"
                      data-testid={`proj-${p.dk_id}`}
                      value={p._slot === "CPT" ? "" : (ov.proj_override ?? "")}
                      disabled={p._slot === "CPT"}
                      onChange={(e) => setField(p.dk_id, "proj_override", e.target.value === "" ? null : Number(e.target.value))}
                      placeholder={Number(baseProj).toFixed(1)}
                      className="w-16 bg-black border border-zinc-800 focus:border-[#39FF14] outline-none px-2 py-0.5 text-xs mono placeholder:text-zinc-600 disabled:opacity-60 disabled:cursor-not-allowed"
                      title={p._slot === "CPT"
                        ? `Captain view · ${Number(baseProj).toFixed(1)} = ${(p._cpt_mult || 1.5).toFixed(2)}x the FLEX projection. Switch to FLEX to edit the projection.`
                        : p.proj_prior
                        ? `Salary + depth-chart prior: ${Number(baseProj).toFixed(2)}. No book market or DK history yet (rookie / new signing) — anchored on what DK-priced peers project, volatility widened. Override if you have a number.`
                        : `Model: ${Number(baseProj).toFixed(2)} — leave blank to keep the model value`}
                    />
                    {p.proj_prior && (
                      <span className="ml-1 mono text-[8px] uppercase tracking-widest text-[#FFCC00]" data-testid={`proj-prior-${p.dk_id}`}
                        title="Salary + depth-chart prior — no book market yet">PRIOR</span>
                    )}
                  </td>
                  <td
                    className={`px-3 py-2.5 font-bold ${valueColor(valueNum)}`}
                    data-testid={`value-${p.dk_id}`}
                    title="Value = (Projection ÷ Salary) × 1000 · ≥ 3.0 elite · 2.0-3.0 standard · < 2.0 overpriced"
                  >
                    {valueNum > 0 ? valueNum.toFixed(2) : "—"}
                  </td>
                  <td className="px-3 py-2.5 text-zinc-500"
                      title={p.field_own_mode === "imported" || p.field_own_mode === "blended"
                        ? `Field own ${Number(p.field_own || 0).toFixed(1)}% — recounted from the imported field lineups${p.field_own_mode === "blended" ? " (blended with the projected number — under 50 lineups imported)" : ""} · projected was ${Number(p.field_own_csv || 0).toFixed(1)}%`
                        : p.field_own_mode === "model"
                          ? "Our internal field-ownership model — no competitor sources loaded for this slate"
                          : "Projected consensus ownership from your uploaded sources"}>
                    {firstPositive(p.market_mean_own, p.our_own).toFixed(1)}%
                    {(p.field_own_mode === "imported" || p.field_own_mode === "blended") && (
                      <span data-testid={`own-recount-${p.dk_id}`}
                        className="ml-1.5 mono text-[9px] px-1 py-[1px] border border-[#39FF14]/50 text-[#39FF14]">
                        {Number(p.field_own || 0).toFixed(0)}%F
                      </span>
                    )}
                    {p.crowd_lock_pct != null && (
                      <span
                        data-testid={`crowd-chip-${p.dk_id}`}
                        title={`WarPaint crowd: ${p.crowd_lock} lock / ${p.crowd_fade} fade (${p.crowd_votes} votes). ${
                          p.crowd_live
                            ? "Live — this is nudging our field-ownership estimate."
                            : "Below the 10-vote gate, so it is display only for now."
                        } Never touches projections.`}
                        className={`ml-1.5 mono text-[9px] px-1 py-[1px] border ${
                          p.crowd_live
                            ? "border-[#FFCC00]/60 text-[#FFCC00] bg-[#FFCC00]/10"
                            : "border-zinc-800 text-zinc-600"
                        }`}
                      >
                        {p.crowd_lock_pct}%L
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2.5 text-zinc-300" data-testid={`ceil-${p.dk_id}`}>
                    {ceilOf(p) > 0 ? ceilOf(p).toFixed(1) : "—"}
                  </td>
                  <td className={`px-3 py-2.5 font-bold ${levColor(levOf(p))}`} data-testid={`lev-${p.dk_id}`}>
                    {levOf(p) > 0 ? levOf(p).toFixed(1) : "—"}
                  </td>
                  <td
                    className={`px-3 py-2.5 font-bold ${levScoreColor(levScoreOf(p))}`}
                    data-testid={`levscore-${p.dk_id}`}
                    title={levScoreOf(p) == null
                      ? "Leverage Score appears once the slate sims have run"
                      : `Optimal ${Number(p.optimal_pct || 0).toFixed(1)}% vs field ${Number(p.field_own_effective ?? p.field_own ?? 0).toFixed(1)}% (${p.field_own_mode === "imported" ? "recounted from imported field lineups" : p.field_own_mode === "blended" ? "imported lineups blended with projected ownership" : p.field_own_mode === "model" ? "our internal field-ownership model — no sources loaded" : "projected ownership"}) → ${p.leverage_tier || ""}`}
                  >
                    {levScoreOf(p) == null ? "—" : `${levScoreOf(p) > 0 ? "+" : ""}${levScoreOf(p).toFixed(1)}`}
                  </td>
                  <td
                    className={`px-3 py-2.5 ${mktDeltaOf(p) == null ? "text-zinc-700" : mktDeltaOf(p) >= 0.5 ? "text-[#39FF14]" : mktDeltaOf(p) <= -0.5 ? "text-[#FF3B30]" : "text-zinc-400"}`}
                    data-testid={`mkt-${p.dk_id}`}
                    title={mktDeltaOf(p) == null ? "No market projection for this player" : `Ours ${firstPositive(p.our_proj, p.projection).toFixed(1)} vs market ${Number(p.market_mean_proj).toFixed(1)}`}
                  >
                    {mktDeltaOf(p) == null ? "—" : `${mktDeltaOf(p) >= 0 ? "+" : ""}${mktDeltaOf(p).toFixed(1)}`}
                  </td>
                  <td className="px-3 py-2.5 text-center">
                    <button
                      data-testid={`lock-${p.dk_id}`}
                      onClick={() => setField(p.dk_id, "lock", !ov.lock)}
                      className={`group relative w-7 h-7 rounded-md inline-flex items-center justify-center transition-all duration-150 active:scale-95 ${
                        ov.lock
                          ? "bg-gradient-to-b from-[#39FF14] to-[#2ee30d] text-black shadow-[0_0_10px_-2px_rgba(57,255,20,0.65)] ring-1 ring-[#39FF14]/60"
                          : "bg-black border border-zinc-800 text-zinc-500 hover:border-[#39FF14] hover:text-[#39FF14] hover:shadow-[0_0_8px_-3px_rgba(57,255,20,0.5)]"
                      }`}
                      title={ov.lock ? "Locked · always in every lineup — click to unlock" : "Lock into every lineup"}
                      aria-pressed={!!ov.lock}
                    >
                      <Lock size={12} weight={ov.lock ? "fill" : "bold"} />
                    </button>
                  </td>
                  <td className="px-3 py-2.5 text-center">
                    {showRemoved && (
                      <button
                        type="button"
                        data-testid={`restore-${p.dk_id}`}
                        onClick={() => {
                          if (isOut(p)) {
                            setRestoreGuard(restoreGuard?.dkId === p.dk_id ? null : { dkId: p.dk_id, proj: ov.proj_override ?? "" });
                            return;
                          }
                          patchOverrides([{ dkId: p.dk_id, key: "exclude", value: false }, { dkId: p.dk_id, key: "exclude_reason", value: null }]);
                          toast.success(`${p.name} restored to the active pool.`);
                        }}
                        aria-expanded={restoreGuard?.dkId === p.dk_id}
                        title={isOut(p) ? `Restore ${p.name} to the active pool despite the ${p.injury_status} tag — you know something the feed doesn't` : `Restore ${p.name} to the active pool`}
                        className="mr-1 w-7 h-7 rounded-md inline-flex items-center justify-center bg-black border border-[#39FF14]/60 text-[#39FF14] hover:bg-[#39FF14] hover:text-black transition-colors active:scale-95"
                      >
                        <ArrowCounterClockwise size={13} weight="bold" />
                      </button>
                    )}
                    <button
                      data-testid={`exclude-${p.dk_id}`}
                      onClick={() => toggleExclude(p, ov)}
                      className={`group relative w-7 h-7 rounded-md inline-flex items-center justify-center transition-all duration-150 active:scale-95 ${
                        ov.exclude
                          ? "bg-gradient-to-b from-[#FF3B30] to-[#d92e24] text-white shadow-[0_0_10px_-2px_rgba(255,59,48,0.65)] ring-1 ring-[#FF3B30]/60"
                          : "bg-black border border-zinc-800 text-zinc-500 hover:border-[#FF3B30] hover:text-[#FF3B30] hover:shadow-[0_0_8px_-3px_rgba(255,59,48,0.5)]"
                      }`}
                      title={ov.exclude
                        ? `You excluded this player${String(ov.exclude_reason || "").startsWith("purge") ? ` via the Min Proj purge (${ov.exclude_reason})` : ""} — the optimizer is banned from using him in ANY lineup. Click to allow again.`
                        : "Exclude from all lineups (hard fade). Prefer MAX EXP to limit a player instead of a full fade."}
                      aria-pressed={!!ov.exclude}
                    >
                      <Prohibit size={12} weight={ov.exclude ? "fill" : "bold"} />
                    </button>
                  </td>
                  <td className="px-3 py-2.5 text-center">
                    <button
                      data-testid={`like-${p.dk_id}`}
                      onClick={() => setField(p.dk_id, "boost_pct", Number(ov.boost_pct) >= 10 ? null : 10)}
                      className={`group relative w-7 h-7 rounded-md inline-flex items-center justify-center transition-all duration-150 active:scale-95 ${
                        Number(ov.boost_pct) >= 10
                          ? "bg-gradient-to-b from-[#FFCC00] to-[#e0b400] text-black shadow-[0_0_10px_-2px_rgba(255,204,0,0.65)] ring-1 ring-[#FFCC00]/60"
                          : "bg-black border border-zinc-800 text-zinc-500 hover:border-[#FFCC00] hover:text-[#FFCC00]"
                      }`}
                      title={Number(ov.boost_pct) >= 10 ? `Liked · +${ov.boost_pct}% boost — click to clear` : "Like · +10% projection boost (soft lean)"}
                      aria-pressed={Number(ov.boost_pct) >= 10}
                    >
                      <Star size={12} weight={Number(ov.boost_pct) >= 10 ? "fill" : "bold"} />
                    </button>
                  </td>
                  {/* Dual-Threat QB flag — only meaningful for QBs, but we
                      still render a dash cell for other positions so the
                      column alignment stays clean and the "why is this
                      empty?" question is answered visually. */}
                  <td className="px-3 py-2.5 text-center">
                    {p.position === "QB" ? (
                      <button
                        data-testid={`dt-${p.dk_id}`}
                        onClick={() => setField(p.dk_id, "dual_threat", !ov.dual_threat)}
                        className={`group relative w-7 h-7 rounded-md inline-flex items-center justify-center transition-all duration-150 active:scale-95 ${
                          ov.dual_threat
                            ? "bg-gradient-to-b from-[#FF8A00] to-[#e07a00] text-black shadow-[0_0_10px_-2px_rgba(255,138,0,0.65)] ring-1 ring-[#FF8A00]/60"
                            : "bg-black border border-zinc-800 text-zinc-500 hover:border-[#FF8A00] hover:text-[#FF8A00] hover:shadow-[0_0_8px_-3px_rgba(255,138,0,0.5)]"
                        }`}
                        title={ov.dual_threat
                          ? "Dual-threat ON · this QB is eligible for `primary_only` (naked QB, no receivers) stacks this week. Click to turn off."
                          : "Click to flag as dual-threat this week (rushing-heavy QB). Only DT-flagged QBs will be used for naked-QB stacks — pocket passers with no stack are a leak."}
                        aria-pressed={!!ov.dual_threat}
                      >
                        <TrendUp size={12} weight={ov.dual_threat ? "fill" : "bold"} />
                      </button>
                    ) : (
                      <span className="text-zinc-700 select-none">—</span>
                    )}
                  </td>
                  <td className="px-3 py-2.5">
                    <input
                      type="number" step="5" min="-50" max="100"
                      value={ov.boost_pct ?? ""}
                      onChange={(e) => setField(p.dk_id, "boost_pct", e.target.value === "" ? null : Number(e.target.value))}
                      placeholder="0"
                      className="w-14 bg-black border border-zinc-800 focus:border-[#39FF14] outline-none px-2 py-0.5 text-xs mono"
                    />
                    <span className="text-[10px] text-zinc-600 ml-1">%</span>
                  </td>
                  <td className="px-3 py-2.5">
                    <input
                      type="number" step="5" min="0" max="100"
                      data-testid={`min-exp-${p.dk_id}`}
                      value={ov.min_exposure_pct ?? ""}
                      onChange={(e) => setField(p.dk_id, "min_exposure_pct", e.target.value === "" ? null : Number(e.target.value))}
                      placeholder="—"
                      className="w-14 bg-black border border-zinc-800 focus:border-[#39FF14] outline-none px-2 py-0.5 text-xs mono"
                    />
                    <span className="text-[10px] text-zinc-600 ml-1">%</span>
                  </td>
                  <td className="px-3 py-2.5">
                    <input
                      type="number" step="5" min="0" max="100"
                      data-testid={`max-exp-${p.dk_id}`}
                      value={ov.max_exposure_pct ?? ""}
                      onChange={(e) => setField(p.dk_id, "max_exposure_pct", e.target.value === "" ? null : Number(e.target.value))}
                      placeholder="—"
                      className="w-14 bg-black border border-zinc-800 focus:border-[#39FF14] outline-none px-2 py-0.5 text-xs mono"
                    />
                    <span className="text-[10px] text-zinc-600 ml-1">%</span>
                  </td>
                  {showdown && (
                    <>
                      <td className="px-3 py-2.5 text-center">
                        <button
                          data-testid={`cpt-lock-${p.dk_id}`}
                          onClick={() => patchOverrides([{ dkId: p.dk_id, key: "cpt_lock", value: !ov.cpt_lock }, { dkId: p.dk_id, key: "cpt_exclude", value: false }])}
                          className={`w-7 h-7 inline-flex items-center justify-center transition-colors ${ov.cpt_lock ? "bg-[#FF3B30] text-black" : "bg-black border border-zinc-800 text-zinc-500 hover:border-[#FF3B30] hover:text-[#FF3B30]"}`}
                          title={ov.cpt_lock ? `Captain pinned · $${Math.round((p.salary || 0) * 1.5).toLocaleString()} at CPT · click to unpin` : `Pin as Captain ($${Math.round((p.salary || 0) * 1.5).toLocaleString()} at CPT)`}
                          aria-pressed={!!ov.cpt_lock}
                        >
                          <Crown size={12} weight={ov.cpt_lock ? "fill" : "bold"} />
                        </button>
                      </td>
                      <td className="px-3 py-2.5 text-center">
                        <button
                          data-testid={`cpt-exclude-${p.dk_id}`}
                          onClick={() => patchOverrides([{ dkId: p.dk_id, key: "cpt_exclude", value: !ov.cpt_exclude }, { dkId: p.dk_id, key: "cpt_lock", value: false }])}
                          className={`w-7 h-7 inline-flex items-center justify-center text-[9px] mono font-bold transition-colors ${ov.cpt_exclude ? "bg-zinc-300 text-black" : "bg-black border border-zinc-800 text-zinc-500 hover:border-zinc-400 hover:text-zinc-200"}`}
                          title={ov.cpt_exclude ? "Never Captain · click to allow" : "Never Captain (FLEX only)"}
                          aria-pressed={!!ov.cpt_exclude}
                        >
                          ✕C
                        </button>
                      </td>
                      <td className="px-3 py-2.5">
                        <input
                          type="number" step="5" min="0" max="100"
                          data-testid={`cpt-min-exp-${p.dk_id}`}
                          value={ov.cpt_min_exposure_pct ?? ""}
                          onChange={(e) => setField(p.dk_id, "cpt_min_exposure_pct", e.target.value === "" ? null : Number(e.target.value))}
                          placeholder="—"
                          title="Force this player to Captain in at least this % of lineups"
                          className="w-14 bg-black border border-zinc-800 focus:border-[#FF3B30] outline-none px-2 py-0.5 text-xs mono"
                        />
                        <span className="text-[10px] text-zinc-600 ml-1">%</span>
                      </td>
                      <td className="px-3 py-2.5">
                        <input
                          type="number" step="5" min="0" max="100"
                          data-testid={`cpt-max-exp-${p.dk_id}`}
                          value={ov.cpt_max_exposure_pct ?? ""}
                          onChange={(e) => setField(p.dk_id, "cpt_max_exposure_pct", e.target.value === "" ? null : Number(e.target.value))}
                          placeholder="—"
                          className="w-14 bg-black border border-zinc-800 focus:border-[#FF3B30] outline-none px-2 py-0.5 text-xs mono"
                        />
                        <span className="text-[10px] text-zinc-600 ml-1">%</span>
                      </td>
                    </>
                  )}
                </tr>
                {showRemoved && restoreGuard?.dkId === p.dk_id && (
                  <tr data-testid={`restore-guard-${p.dk_id}`} className="bg-[#FF3B30]/5">
                    <td colSpan={16} className="px-3 py-2.5">
                      <div className="flex flex-wrap items-center gap-3 border border-[#FF3B30]/40 bg-black px-3 py-2">
                        <Warning size={14} weight="fill" className="text-[#FF3B30]" />
                        <div className="mono text-[11px] text-zinc-200">
                          <span className="font-black text-[#FF3B30]">{p.name}</span> is <span className="font-black">{p.injury_status ? String(p.injury_status).toUpperCase() : (p.unplayable_reason === "BACKUP_QB" ? "A BACKUP" : "UNPLAYABLE")}</span> per the feed — the model projects 0.0. Restoring requires your own number.
                        </div>
                        <label className="flex items-center gap-2 mono text-[10px] uppercase tracking-widest text-zinc-400">
                          Proj
                          <input
                            type="number" step="0.5" min="0.5" autoFocus
                            data-testid={`restore-guard-proj-${p.dk_id}`}
                            value={restoreGuard.proj}
                            onChange={(e) => setRestoreGuard({ dkId: p.dk_id, proj: e.target.value })}
                            onKeyDown={(e) => { if (e.key === "Escape") setRestoreGuard(null); }}
                            placeholder={firstPositive(p.market_mean_proj, p.avg_ppg) > 0 ? firstPositive(p.market_mean_proj, p.avg_ppg).toFixed(1) : "0.0"}
                            className="w-20 bg-black border border-[#FF3B30]/60 focus:border-[#39FF14] outline-none px-2 py-0.5 text-xs mono text-white placeholder:text-zinc-600"
                          />
                        </label>
                        <button
                          type="button"
                          data-testid={`restore-guard-confirm-${p.dk_id}`}
                          disabled={!(Number(restoreGuard.proj) > 0)}
                          onClick={() => {
                            const n = Number(restoreGuard.proj);
                            if (!(n > 0)) return;
                            const patches = [{ dkId: p.dk_id, key: "restore", value: true }, { dkId: p.dk_id, key: "proj_override", value: n }];
                            if (ov.exclude) patches.push({ dkId: p.dk_id, key: "exclude", value: false }, { dkId: p.dk_id, key: "exclude_reason", value: null });
                            patchOverrides(patches);
                            setRestoreGuard(null);
                            toast.success(`${p.name} restored at ${n.toFixed(1)} pts — your projection overrides the feed's 0.0.`);
                          }}
                          className="mono text-[10px] font-black uppercase tracking-widest px-3 py-1 border border-[#39FF14]/70 text-[#39FF14] hover:bg-[#39FF14] hover:text-black disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-[#39FF14] transition-colors"
                        >
                          Restore
                        </button>
                        <button type="button" data-testid={`restore-guard-cancel-${p.dk_id}`} onClick={() => setRestoreGuard(null)}
                                className="mono text-[10px] uppercase tracking-widest text-zinc-500 hover:text-zinc-200">
                          Cancel
                        </button>
                        {p.market_mean_proj > 0 && (
                          <span className="mono text-[9px] text-zinc-500">books quote him at {Number(p.market_mean_proj).toFixed(1)}</span>
                        )}
                      </div>
                    </td>
                  </tr>
                )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function SortableTh({ label, col, sortBy, sortDir, onClick, title }) {
  const active = sortBy === col;
  const Icon = active ? (sortDir === "asc" ? CaretUp : CaretDown) : CaretUpDown;
  return (
    <th
      className="px-3 py-2 cursor-pointer select-none hover:text-white transition-colors"
      onClick={() => onClick(col)}
      title={title || `Sort by ${label}`}
      data-testid={`pool-sort-${col}`}
    >
      <span className="inline-flex items-center gap-1">
        {label}
        <Icon size={10} weight={active ? "fill" : "regular"} className={active ? "text-[#39FF14]" : "text-zinc-600"} />
      </span>
    </th>
  );
}


// Preset intensities for the SWAP dropdown. Full = hard swap (exclude chalk,
// lock pivot). Leverage = cap chalk at 20% max exposure, require pivot in 30%
// of lineups. Soft = 40/25. Values are the min/max exposure % applied to the
// pivot's min_exposure_pct and the chalk's max_exposure_pct override keys.
const SWAP_INTENSITIES = {
  full: {
    label: "Full swap",
    hint: "Exclude chalk + lock pivot into every lineup",
    apply: (chalkId, pivotId) => [
      { dkId: chalkId, key: "lock", value: false },
      { dkId: chalkId, key: "exclude", value: true },
      { dkId: chalkId, key: "max_exposure_pct", value: null },
      { dkId: pivotId, key: "exclude", value: false },
      { dkId: pivotId, key: "lock", value: true },
      { dkId: pivotId, key: "min_exposure_pct", value: null },
    ],
  },
  leverage: {
    label: "Leverage bet (20/30)",
    hint: "Cap chalk at 20% max · require pivot in 30% of lineups",
    apply: (chalkId, pivotId) => [
      { dkId: chalkId, key: "lock", value: false },
      { dkId: chalkId, key: "exclude", value: false },
      { dkId: chalkId, key: "max_exposure_pct", value: 20 },
      { dkId: pivotId, key: "exclude", value: false },
      { dkId: pivotId, key: "lock", value: false },
      { dkId: pivotId, key: "min_exposure_pct", value: 30 },
    ],
  },
  soft: {
    label: "Soft pivot (40/25)",
    hint: "Cap chalk at 40% max · require pivot in 25% of lineups",
    apply: (chalkId, pivotId) => [
      { dkId: chalkId, key: "lock", value: false },
      { dkId: chalkId, key: "exclude", value: false },
      { dkId: chalkId, key: "max_exposure_pct", value: 40 },
      { dkId: pivotId, key: "exclude", value: false },
      { dkId: pivotId, key: "lock", value: false },
      { dkId: pivotId, key: "min_exposure_pct", value: 25 },
    ],
  },
};

// Projection nudge presets — organic influence without a hard swap. Applied
// as proj_override = baseline_proj + delta, so the optimizer sees the pivot
// as slightly more attractive and the chalk as slightly less, letting the
// solver naturally shift lineups without the user making a binary decision.
const NUDGE_PRESETS = {
  nudge: { label: "Nudge (+1.5 / -0.5)", pivotDelta: 1.5, chalkDelta: -0.5 },
  boost: { label: "Boost (+3.0 / -1.0)", pivotDelta: 3.0, chalkDelta: -1.0 },
  big:   { label: "Big Boost (+5.0 / -2.0)", pivotDelta: 5.0, chalkDelta: -2.0 },
};

function PivotSuggestionsPopover({ chalkId, chalkName, chalkBaseProj, overrides, patchOverrides, onClose }) {
  const [loading, setLoading] = useState(true);
  const [pivots, setPivots] = useState([]);
  const [error, setError] = useState(null);
  const [intensityKey, setIntensityKey] = useState("full");
  const [nudgeKey, setNudgeKey] = useState("nudge");
  const boxRef = React.useRef(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const r = await api.aiPivotSuggestions(chalkId, 5);
        if (cancelled) return;
        setPivots(r.pivots || []);
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.detail || "Failed to load pivots");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [chalkId]);

  useEffect(() => {
    const onDocClick = (e) => {
      if (e.target?.closest && e.target.closest('[data-testid^="chalk-flag-"]')) return;
      if (boxRef.current && !boxRef.current.contains(e.target)) onClose();
    };
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [onClose]);

  const applySwap = (pivot) => {
    const preset = SWAP_INTENSITIES[intensityKey] || SWAP_INTENSITIES.full;
    patchOverrides(preset.apply(chalkId, pivot.dk_id));
    toast.success(`${preset.label}: ${chalkName} ↔ ${pivot.name}`);
    onClose();
  };

  const applyNudge = (pivot) => {
    const preset = NUDGE_PRESETS[nudgeKey] || NUDGE_PRESETS.nudge;
    const pivotBase = Number(pivot.proj || 0);
    const chalkBase = Number(chalkBaseProj || 0);
    const pivotOverride = Math.max(0, Math.round((pivotBase + preset.pivotDelta) * 10) / 10);
    const chalkOverride = Math.max(0, Math.round((chalkBase + preset.chalkDelta) * 10) / 10);
    patchOverrides([
      { dkId: pivot.dk_id, key: "proj_override", value: pivotOverride },
      { dkId: chalkId, key: "proj_override", value: chalkOverride },
    ]);
    toast.success(
      `${preset.label}: ${pivot.name} → ${pivotOverride.toFixed(1)} · ${chalkName} → ${chalkOverride.toFixed(1)}`
    );
  };

  const setExposure = (pivotId, key, value) => {
    // Empty input clears the override; otherwise clamp 0-100.
    if (value === "" || value === null || value === undefined) {
      patchOverrides([{ dkId: pivotId, key, value: null }]);
      return;
    }
    const num = Math.max(0, Math.min(100, Number(value)));
    if (Number.isNaN(num)) return;
    patchOverrides([{ dkId: pivotId, key, value: num }]);
  };

  return (
    <div
      ref={boxRef}
      data-testid={`pivot-popover-${chalkId}`}
      className="absolute top-full left-0 mt-1 z-50 w-[520px] bg-[#0a0a0a] border border-[#ef4444]/50 shadow-2xl"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="flex items-center justify-between px-3 py-2 border-b border-zinc-900 bg-black">
        <div className="flex items-center gap-2">
          <Warning size={11} weight="fill" className="text-[#ef4444]" />
          <span className="mono text-[9px] uppercase tracking-widest text-zinc-300">
            Pivot Options — {chalkName}
          </span>
        </div>
        <button
          type="button"
          onClick={onClose}
          data-testid={`pivot-popover-close-${chalkId}`}
          className="text-zinc-500 hover:text-white transition-colors"
          aria-label="Close pivot suggestions"
        >
          <X size={12} />
        </button>
      </div>

      {/* Preset selectors row — controls apply globally to whatever pivot the
          user clicks Swap or Nudge on. Cheaper than a per-row picker. */}
      {!loading && !error && pivots.length > 0 && (
        <div className="grid grid-cols-2 gap-2 px-3 py-2 border-b border-zinc-900 bg-black/60">
          <label className="flex flex-col gap-1">
            <span className="mono text-[8px] uppercase tracking-widest text-zinc-500">Swap intensity</span>
            <select
              data-testid={`pivot-intensity-${chalkId}`}
              value={intensityKey}
              onChange={(e) => setIntensityKey(e.target.value)}
              title={SWAP_INTENSITIES[intensityKey]?.hint}
              className="bg-zinc-950 border border-zinc-800 text-[10px] text-white px-1.5 py-1 focus:border-[#39FF14] focus:outline-none"
            >
              {Object.entries(SWAP_INTENSITIES).map(([k, v]) => (
                <option key={k} value={k}>{v.label}</option>
              ))}
            </select>
          </label>
          <label className="flex flex-col gap-1">
            <span className="mono text-[8px] uppercase tracking-widest text-zinc-500">Projection nudge</span>
            <select
              data-testid={`pivot-nudge-preset-${chalkId}`}
              value={nudgeKey}
              onChange={(e) => setNudgeKey(e.target.value)}
              title="Boost pivot projection and trim chalk projection organically — no hard swap."
              className="bg-zinc-950 border border-zinc-800 text-[10px] text-white px-1.5 py-1 focus:border-[#FF8A00] focus:outline-none"
            >
              {Object.entries(NUDGE_PRESETS).map(([k, v]) => (
                <option key={k} value={k}>{v.label}</option>
              ))}
            </select>
          </label>
        </div>
      )}

      <div className="p-2 space-y-1.5" data-testid={`pivot-popover-list-${chalkId}`}>
        {loading && <div className="text-[10px] text-zinc-500 mono text-center py-4">Loading pivots…</div>}
        {!loading && error && <div className="text-[10px] text-[#ef4444] mono text-center py-4">{error}</div>}
        {!loading && !error && pivots.length === 0 && (
          <div className="text-[10px] text-zinc-500 mono text-center py-4">
            No sub-5%-owned pivots available at this salary/position.
          </div>
        )}
        {!loading && !error && pivots.map((piv, i) => {
          const pivOv = overrides[piv.dk_id] || {};
          return (
            <div
              key={piv.dk_id}
              data-testid={`pivot-popover-row-${i + 1}`}
              className="border border-zinc-900 bg-zinc-950/50 hover:border-[#39FF14]/40 transition-colors"
            >
              {/* Info line */}
              <div className="flex items-center gap-2 px-2 pt-1.5">
                <span className="mono text-[9px] text-zinc-500 w-4 shrink-0">#{i + 1}</span>
                <div className="flex-1 min-w-0">
                  <div className="text-[11px] text-white font-medium truncate">{piv.name}</div>
                  <div className="mono text-[9px] text-zinc-500 uppercase">
                    {piv.pos} · {piv.team} · ${piv.salary}
                    {piv.salary_delta !== 0 && (
                      <span className={piv.salary_delta > 0 ? "text-zinc-400" : "text-[#39FF14]"}>
                        {" "}({piv.salary_delta > 0 ? "+" : ""}{piv.salary_delta})
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex flex-col items-end mono text-[9px] shrink-0">
                  <span className="text-white">{piv.proj.toFixed(1)}</span>
                  <span className="text-zinc-500">{piv.own.toFixed(1)}%</span>
                </div>
              </div>
              {/* Action line */}
              <div className="flex items-center gap-1.5 px-2 py-1.5 mt-1 border-t border-zinc-900 bg-black/30">
                <button
                  type="button"
                  onClick={() => applySwap(piv)}
                  data-testid={`pivot-swap-${piv.dk_id}`}
                  className="px-2 py-1 text-[9px] uppercase tracking-widest font-bold bg-[#39FF14] text-black hover:bg-[#2ee30d] transition-colors"
                  title={SWAP_INTENSITIES[intensityKey]?.hint}
                >
                  Swap
                </button>
                <button
                  type="button"
                  onClick={() => applyNudge(piv)}
                  data-testid={`pivot-nudge-${piv.dk_id}`}
                  className="px-2 py-1 text-[9px] uppercase tracking-widest font-bold bg-[#FF8A00] text-black hover:bg-[#e77b00] transition-colors"
                  title={`Set proj_override: ${piv.name} +${NUDGE_PRESETS[nudgeKey].pivotDelta}, ${chalkName} ${NUDGE_PRESETS[nudgeKey].chalkDelta}`}
                >
                  Nudge
                </button>
                <div className="flex-1" />
                <label className="flex items-center gap-1" title="Minimum lineup exposure % for this pivot">
                  <span className="mono text-[8px] uppercase tracking-widest text-zinc-500">Min</span>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    placeholder="—"
                    value={pivOv.min_exposure_pct ?? ""}
                    onChange={(e) => setExposure(piv.dk_id, "min_exposure_pct", e.target.value)}
                    data-testid={`pivot-min-exp-${piv.dk_id}`}
                    className="w-11 bg-zinc-950 border border-zinc-800 text-[10px] text-white px-1 py-0.5 focus:border-[#39FF14] focus:outline-none"
                  />
                </label>
                <label className="flex items-center gap-1" title="Maximum lineup exposure % for this pivot">
                  <span className="mono text-[8px] uppercase tracking-widest text-zinc-500">Max</span>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    placeholder="—"
                    value={pivOv.max_exposure_pct ?? ""}
                    onChange={(e) => setExposure(piv.dk_id, "max_exposure_pct", e.target.value)}
                    data-testid={`pivot-max-exp-${piv.dk_id}`}
                    className="w-11 bg-zinc-950 border border-zinc-800 text-[10px] text-white px-1 py-0.5 focus:border-[#39FF14] focus:outline-none"
                  />
                </label>
              </div>
            </div>
          );
        })}
      </div>
      <div className="px-3 py-1.5 border-t border-zinc-900 bg-black">
        <div className="mono text-[8px] uppercase tracking-widest text-zinc-600 leading-relaxed">
          <span className="text-[#39FF14]">Swap</span> applies the selected intensity to both players ·
          <span className="text-[#FF8A00]"> Nudge</span> adjusts projections organically ·
          Min/Max caps this pivot's lineup exposure directly.
        </div>
      </div>
    </div>
  );
}

/**
 * DualThreatWeeklyBanner
 *
 * Sits above the Player Pool table and surfaces the "Dual-Threat QB" workflow
 * every time a user opens the Optimizer. Three jobs:
 *   1. Show a live count of currently-flagged DT QBs so users at a glance
 *      know whether they've done this week's tuning.
 *   2. Provide a one-click "How this works" affordance (opens the guide
 *      modal) — critical for first-run discoverability.
 *   3. Nudge users toward updating flags each week ("Update every Sunday")
 *      without being intrusive if they've already flagged something.
 */
function DualThreatWeeklyBanner({ overrides, pool, onOpenGuide }) {
  const flaggedIds = Object.entries(overrides || {})
    .filter(([, ov]) => ov && ov.dual_threat === true)
    .map(([id]) => String(id));
  const flaggedQbs = (pool || []).filter(
    (p) => p.position === "QB" && flaggedIds.includes(String(p.dk_id))
  );
  const hasFlagged = flaggedQbs.length > 0;
  return (
    <div
      data-testid="dt-weekly-banner"
      className={`border ${hasFlagged ? "border-[#FF8A00]/40 bg-[#FF8A00]/5" : "border-zinc-800 bg-[#0d0d0d]"} px-4 py-2.5 mb-2 flex items-center gap-3 text-xs`}
    >
      <div className={`w-8 h-8 border ${hasFlagged ? "border-[#FF8A00] bg-[#FF8A00]/10" : "border-zinc-700 bg-zinc-900"} flex items-center justify-center flex-shrink-0`}>
        <TrendUp size={16} weight="fill" className={hasFlagged ? "text-[#FF8A00]" : "text-zinc-500"} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="mono uppercase tracking-widest text-[10px] font-bold text-[#FF8A00]">
            Dual-Threat QB Gate
          </span>
          {hasFlagged ? (
            <span
              data-testid="dt-count-badge"
              className="mono text-[10px] uppercase tracking-widest bg-[#FF8A00] text-black px-1.5 py-0.5 font-bold"
            >
              {flaggedQbs.length} flagged
            </span>
          ) : (
            <span className="mono text-[10px] uppercase tracking-widest text-[#FFCC00]">
              0 flagged · naked-QB stacks disabled
            </span>
          )}
        </div>
        <div className="text-zinc-400 text-[11px] mt-0.5 leading-relaxed truncate">
          {hasFlagged ? (
            <>
              This week's rushing QBs: <span className="text-white">{flaggedQbs.map((q) => q.name).slice(0, 4).join(", ")}</span>
              {flaggedQbs.length > 4 ? ` +${flaggedQbs.length - 4} more` : ""}. Update before every slate lock — the app re-checks, but you know your news.
            </>
          ) : (
            <>Check the <span className="text-[#FF8A00]">DT</span> box for rushing QBs each week — otherwise `primary_only` (naked QB) stacks get suppressed.</>
          )}
        </div>
      </div>
      <button
        onClick={onOpenGuide}
        data-testid="dt-open-guide"
        className="text-[10px] mono uppercase tracking-widest text-[#FF8A00] hover:text-white border-l border-zinc-800 pl-3 flex-shrink-0"
        title="Learn what Dual-Threat means and when to flag which QBs"
      >
        How this works →
      </button>
    </div>
  );
}

/**
 * DualThreatGuideModal
 *
 * Full-page explainer users open from the banner. Covers WHY (correlation
 * theory), WHEN (weekly cadence, Sunday AM), WHICH QBs to look for
 * (rushing signals, red flags), and HOW (one click on the DT chip).
 * First-run friendly, sharp-user skimmable.
 */
function DualThreatGuideModal({ open, onClose }) {
  if (!open) return null;
  return (
    <div
      data-testid="dt-guide-modal"
      className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-[#0d0d0d] border border-[#FF8A00] max-w-2xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-zinc-800">
          <div className="flex items-center gap-2">
            <TrendUp size={18} weight="fill" className="text-[#FF8A00]" />
            <h3 className="heading font-bold uppercase tracking-widest text-white text-sm">
              Dual-Threat QB Gate · Weekly Workflow
            </h3>
          </div>
          <button
            onClick={onClose}
            data-testid="dt-guide-close"
            className="text-zinc-500 hover:text-white"
            aria-label="Close"
          >
            <X size={16} />
          </button>
        </div>

        <div className="px-6 py-5 space-y-5 text-sm text-zinc-300 leading-relaxed">
          <section>
            <div className="mono text-[10px] uppercase tracking-widest text-[#39FF14] mb-1">Why this exists</div>
            <p>
              The optimizer builds three shapes: <span className="text-white">primary_only</span> (naked QB, no
              stacked receivers), <span className="text-white">primary+secondary</span>, and
              <span className="text-white"> primary+secondary+tertiary</span>. The naked QB shape only correlates
              with a ceiling for rushing-heavy QBs whose fantasy production comes from carries + rush TDs — not
              from throwing to their WRs. Pairing a pocket passer with `primary_only` is a documented DFS leak.
            </p>
          </section>

          <section>
            <div className="mono text-[10px] uppercase tracking-widest text-[#FF8A00] mb-1">The Gate</div>
            <p>
              Only QBs you flag as <span className="text-[#FF8A00] font-bold">DT</span> in the Player Pool are
              eligible for `primary_only` schedule slots. If <span className="text-white">zero</span> QBs are
              flagged, the optimizer refuses to build any naked-QB lineups at all — safer default than
              generating a leak.
            </p>
          </section>

          <section>
            <div className="mono text-[10px] uppercase tracking-widest text-[#39FF14] mb-1">Weekly workflow</div>
            <ol className="list-decimal ml-5 space-y-1">
              <li>Before every slate lock — Thu, Sun early/late, Mon — after checking injury reports & inactives</li>
              <li>Scroll the Player Pool to <span className="text-white">QB</span> position filter</li>
              <li>Click the <span className="text-[#FF8A00] font-bold">DT</span> chip for QBs you expect to rush</li>
              <li>Un-check any DT QB who's sitting the week or facing a spy-heavy defense</li>
            </ol>
          </section>

          <section>
            <div className="mono text-[10px] uppercase tracking-widest text-[#FFCC00] mb-1">Green flags (usually DT)</div>
            <ul className="list-disc ml-5 space-y-0.5 text-zinc-400">
              <li>Rushing lines of <span className="text-white">≥ 5 attempts</span> per game trend</li>
              <li>Designed run scheme (Sean Payton, Kevin O'Connell, Sirianni, Harbaugh systems)</li>
              <li>QBs who broke a rushing TD in any of the last 3 weeks</li>
              <li>Read-option or RPO-heavy offenses (Miami, Baltimore, Philly, Buffalo, Chicago)</li>
            </ul>
          </section>

          <section>
            <div className="mono text-[10px] uppercase tracking-widest text-[#FF3B30] mb-1">Red flags (un-check)</div>
            <ul className="list-disc ml-5 space-y-0.5 text-zinc-400">
              <li>Injury note: hamstring, groin, ankle — rushing floor evaporates</li>
              <li>Blowout risk (large favorite ≥ 10 pts) — game script kills rush attempts</li>
              <li>Elite spy defense (SF, BAL, PIT top-5 QB rush yards allowed)</li>
              <li>Coach explicitly comments "we need to protect him" in pressers</li>
            </ul>
          </section>

          <div className="mt-2 pt-3 border-t border-zinc-800 flex items-center justify-between text-[10px] mono uppercase tracking-widest">
            <span className="text-zinc-500">Tip · flagging is per-session · resets are safe</span>
            <button
              onClick={onClose}
              data-testid="dt-guide-got-it"
              className="bg-[#FF8A00] hover:bg-[#e67a00] text-black font-bold px-4 py-1.5 transition-colors"
            >
              Got it
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

